import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import sampleData from '../../data/finFutAll.json';
import cftcRules from '../../data/cftcRules.json';
import styles from './SideDrawer.module.css';
import moment from 'moment-timezone';
import { useAppContext } from '../../App';
import {
  computeNet,
  computeNetPctOI,
  computePercentiles,
  computeZScores,
  getPositioningInsight,
} from './positionView';
import { rollingPercentileBands } from './positioningBands.js';
import AlertsPanel from './AlertsPanel.tsx';
import ReadinessBadge from './ReadinessBadge.tsx';
import CftcTooltipCard from './CftcTooltipCard.jsx';
import CftcDashboardLayout from '../cftc/CftcDashboardLayout';
import RegimePill from '../cftc/RegimePill';
import StatusPill from '../cftc/StatusPill';
import SummaryCard from '../cftc/SummaryCard';
import DeskReadCard from '../cftc/DeskReadCard';
import Plot from 'react-plotly.js';
import { FaChevronLeft, FaChevronRight, FaPen } from 'react-icons/fa';
import { HiOutlineSparkles } from 'react-icons/hi2';
import GrossActivityBadge from './GrossActivityBadge.tsx';
import SignalsOnboardingTour from './SignalsOnboardingTour.tsx';
import { buildSignalsOnboardingSteps, buildCftcSummaryOnboardingSteps } from './signalsOnboardingSteps.ts';
import { buildPositionTrendsOnboardingSteps } from './positionTrendsOnboardingSteps.ts';
import {
  createSignalsOnboardingState,
  getCloseSignalsOnboardingState,
  getDoneSignalsOnboardingState,
  getReplaySignalsOnboardingState,
  getSkipSignalsOnboardingState,
  readSignalsOnboardingSeen,
  shouldAutoOpenSignalsOnboarding,
  writeSignalsOnboardingSeen,
} from './signalsOnboardingState.ts';
import {
  createCftcSummaryOnboardingState,
  readCftcSummaryOnboardingSeen,
  writeCftcSummaryOnboardingSeen,
  shouldAutoOpenCftcSummaryOnboarding,
  getReplayCftcSummaryOnboardingState,
  getCloseCftcSummaryOnboardingState,
  getSkipCftcSummaryOnboardingState,
  getDoneCftcSummaryOnboardingState,
} from './cftcSummaryOnboardingState.ts';
import {
  createPositionTrendsOnboardingState,
  getClosePositionTrendsOnboardingState,
  getDonePositionTrendsOnboardingState,
  getReplayPositionTrendsOnboardingState,
  getSkipPositionTrendsOnboardingState,
  readPositionTrendsOnboardingSeen,
  writePositionTrendsOnboardingSeen,
} from './positionTrendsOnboardingState.ts';
import { useCftcSignals } from './useCftcSignals.ts';
import { usePriceReferenceMode } from './usePriceReferenceMode.ts';
import { computeDaysSinceCftcAsOf, computeLiveDecay, selectPriceAsOfDate } from './priceReferenceUtils.ts';
import { buildDailySignals } from './cftcSignalsUtils.ts';
import {
  computeDelta,
  computeLaggedCorrelation,
  computeReturns,
  getConfidenceBadge,
  getCorrelationInterpretation,
  getCorrelationStrengthBucket,
  hasConsistentSignAcrossLags,
} from './correlationUtils';
import {
  classifyGrossActivity,
  computeGrossCombinedPctOI,
  computePercentile,
} from './grossActivityUtils.ts';

const toNumber = (value) => {
  if (value == null) {
    return 0;
  }

  const cleaned = `${value}`.replace(/[^\d.-]/g, '');
  const parsed = Number(cleaned);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatNumber = (value) => {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(value);
};

const formatSignedNumber = (value) => {
  const numeric = Number(value) || 0;
  const sign = numeric > 0 ? '+' : numeric < 0 ? '−' : '';
  return `${sign}${formatNumber(Math.abs(numeric))}`;
};

const formatChange = (value) => {
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${Math.abs(value)}`.replace(/^(\+|-)?0$/, '0');
};

const formatPrice = (value) => {
  if (value == null || value === '') {
    return '—';
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 5,
  }).format(numeric);
};

const formatPercent = (value) => {
  const numeric = toNumber(value);
  return `${numeric.toFixed(1)}%`;
};

const toRgba = (hex, alpha) => {
  const cleaned = hex.replace('#', '');
  if (cleaned.length !== 6) {
    return `rgba(255, 255, 255, ${alpha})`;
  }
  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatViewValue = (value, view) => {
  if (value == null || value === '') {
    return '—';
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return '—';
  }
  if (view === 'net_pct_oi' || view === 'percentile') {
    return `${numeric.toFixed(2)}%`;
  }
  if (view === 'zscore') {
    return numeric.toFixed(2);
  }
  return formatNumber(numeric);
};



const POSITION_VIEW_OPTIONS = [
  { value: 'long', label: 'Long' },
  { value: 'short', label: 'Short' },
  { value: 'net', label: 'Net' },
  { value: 'net_pct_oi', label: 'Net % OI' },
  { value: 'zscore', label: 'Z-score' },
];

const POSITION_VIEW_DESCRIPTIONS = {
  long: 'Long positions by category.',
  short: 'Short positions by category.',
  net: 'Net = Long – Short.',
  net_pct_oi: 'Net % OI = (Long – Short) / Open Interest × 100.',
  zscore: 'Z-score of Net vs selected range: (Net – mean) / std.',
};

const CORRELATION_METRIC_OPTIONS = [
  { value: 'delta_net', label: 'Delta Net' },
  { value: 'delta_net_pct_oi', label: 'Delta Net % OI' },
];

const CORRELATION_METHOD_OPTIONS = [
  { value: 'pearson', label: 'Pearson' },
  { value: 'spearman', label: 'Spearman' },
];

const CORRELATION_HORIZON_OPTIONS = [1, 2, 3, 4];


const PURCHASER_GROUPS = [
  {
    id: 'Dealer',
    label: 'Dealer',
    longKey: 'Dealer_Positions_Long_All',
    shortKey: 'Dealer_Positions_Short_All',
    spreadKey: 'Dealer_Positions_Spread_All',
    changeLongKey: 'Change_in_Dealer_Long_All',
    changeShortKey: 'Change_in_Dealer_Short_All',
    pctLongKey: 'Pct_of_OI_Dealer_Long_All',
    pctShortKey: 'Pct_of_OI_Dealer_Short_All',
  },
  {
    id: 'AssetManager',
    label: 'Asset Manager',
    longKey: 'Asset_Mgr_Positions_Long_All',
    shortKey: 'Asset_Mgr_Positions_Short_All',
    spreadKey: 'Asset_Mgr_Positions_Spread_All',
    changeLongKey: 'Change_in_Asset_Mgr_Long_All',
    changeShortKey: 'Change_in_Asset_Mgr_Short_All',
    pctLongKey: 'Pct_of_OI_Asset_Mgr_Long_All',
    pctShortKey: 'Pct_of_OI_Asset_Mgr_Short_All',
  },
  {
    id: 'LeveragedMoney',
    label: 'Leveraged Funds',
    longKey: 'Lev_Money_Positions_Long_All',
    shortKey: 'Lev_Money_Positions_Short_All',
    spreadKey: 'Lev_Money_Positions_Spread_All',
    changeLongKey: 'Change_in_Lev_Money_Long_All',
    changeShortKey: 'Change_in_Lev_Money_Short_All',
    pctLongKey: 'Pct_of_OI_Lev_Money_Long_All',
    pctShortKey: 'Pct_of_OI_Lev_Money_Short_All',
  },
];

const SNAPSHOT_GROUPS = [
  ...PURCHASER_GROUPS,
  {
    id: 'OtherReportables',
    label: 'Other Reportables',
    longKey: 'Other_Rept_Positions_Long_All',
    shortKey: 'Other_Rept_Positions_Short_All',
    spreadKey: 'Other_Rept_Positions_Spread_All',
  },
  {
    id: 'NonReportable',
    label: 'Nonreportable',
    longKey: 'NonRept_Positions_Long_All',
    shortKey: 'NonRept_Positions_Short_All',
  },
];

const SNAPSHOT_CHANGE_GROUPS = [
  ...PURCHASER_GROUPS,
  {
    id: 'OtherReportables',
    label: 'Other Reportables',
    changeLongKey: 'Change_in_Other_Rept_Long_All',
    changeShortKey: 'Change_in_Other_Rept_Short_All',
  },
  {
    id: 'NonReportable',
    label: 'Nonreportable',
    changeLongKey: 'Change_in_NonRept_Long_All',
    changeShortKey: 'Change_in_NonRept_Short_All',
  },
];

const SNAPSHOT_GROUP_BY_ID = SNAPSHOT_GROUPS.reduce((acc, group) => {
  acc[group.id] = group;
  return acc;
}, {});

const IMPACT_REPORT_PARTICIPANT_GROUPS = SNAPSHOT_CHANGE_GROUPS.map((group) => {
  const snapshotGroup = SNAPSHOT_GROUP_BY_ID[group.id];
  return {
    id: group.id,
    label: group.label,
    longKey: snapshotGroup?.longKey,
    shortKey: snapshotGroup?.shortKey,
    changeLongKey: group.changeLongKey,
    changeShortKey: group.changeShortKey,
  };
}).filter((group) => group.longKey && group.shortKey);

const DEFAULT_IMPACT_REPORT_GROUP_IDS = ['AssetManager', 'LeveragedMoney'];

const IMPACT_REPORT_SCOPE_OPTIONS = [
  { value: 'selected_market_group', label: 'Selected market group' },
  { value: 'current_contract', label: 'Current contract' },
];

const IMPACT_REPORT_LOOKBACK_OPTIONS = [
  { value: 1, label: '1 week' },
  { value: 4, label: '4 weeks' },
  { value: 13, label: '13 weeks' },
];

const getReportDateValue = (row) =>
  row?.['Report_Date_as_YYYY-MM-DD'] ||
  row?.['Report_Date_as_YYYY_MM_DD'] ||
  row?.As_of_Date_In_Form_YYMMDD ||
  '';

const toIsoReportDate = (value) => {
  if (!value) {
    return '';
  }
  const date = moment(value, ['YYYY-MM-DD', 'YYMMDD'], true);
  return date.isValid() ? date.format('YYYY-MM-DD') : '';
};

const getRowReportDateIso = (row) => toIsoReportDate(getReportDateValue(row));

const formatSignedPercent = (value, decimals = 2) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '0.00%';
  }
  const sign = numeric > 0 ? '+' : numeric < 0 ? '-' : '';
  return `${sign}${Math.abs(numeric).toFixed(decimals)}%`;
};

const describeBias = (flowScorePctOi) => {
  if (flowScorePctOi >= 0.12) {
    return 'Bullish pressure';
  }
  if (flowScorePctOi <= -0.12) {
    return 'Bearish pressure';
  }
  return 'Balanced';
};

const classifyShortingCaution = ({ flowScorePctOi, weeklyFlowPctOi, netPctOi }) => {
  if (
    flowScorePctOi >= 0.18 ||
    (flowScorePctOi >= 0.12 && netPctOi >= 3) ||
    (weeklyFlowPctOi >= 0.15 && netPctOi >= 0)
  ) {
    return 'High';
  }
  if (
    flowScorePctOi >= 0.08 ||
    (weeklyFlowPctOi >= 0.08 && netPctOi >= -1)
  ) {
    return 'Moderate';
  }
  return 'Low';
};

const classifyLongingCaution = ({ flowScorePctOi, weeklyFlowPctOi, netPctOi }) => {
  if (
    flowScorePctOi <= -0.18 ||
    (flowScorePctOi <= -0.12 && netPctOi <= -3) ||
    (weeklyFlowPctOi <= -0.15 && netPctOi <= 0)
  ) {
    return 'High';
  }
  if (
    flowScorePctOi <= -0.08 ||
    (weeklyFlowPctOi <= -0.08 && netPctOi <= 1)
  ) {
    return 'Moderate';
  }
  return 'Low';
};

const computeGroupNetAndFlow = (row, groupConfig) => {
  const long = toNumber(row?.[groupConfig.longKey]);
  const short = toNumber(row?.[groupConfig.shortKey]);
  const changeLong = toNumber(row?.[groupConfig.changeLongKey]);
  const changeShort = toNumber(row?.[groupConfig.changeShortKey]);
  return {
    net: long - short,
    weeklyDeltaNet: changeLong - changeShort,
  };
};

const PURCHASER_COLORS = {
  Dealer: '#4dabf5',
  AssetManager: '#82c91e',
  LeveragedMoney: '#f06595',
  OtherReportables: 'var(--color-warning)',
  NonReportable: 'var(--color-accent)',
};

const TRADINGVIEW_SOURCES = ['OANDA', 'FX_IDC', 'FOREXCOM'];
const TRADINGVIEW_INTERVALS = [
  { label: 'D', value: 'D' },
  { label: 'W', value: 'W' },
  { label: 'M', value: 'M' },
];
const TRADINGVIEW_RANGES = [
  { label: '1M', value: '1M' },
  { label: '3M', value: '3M' },
  { label: '6M', value: '6M' },
  { label: '12M', value: '12M' },
  { label: 'YTD', value: 'YTD' },
  { label: 'All', value: 'ALL' },
];

const CORRELATION_PRESETS = {
  fx: { metric: 'delta_net', horizon: 2, lag: 2, method: 'spearman' },
  rates: { metric: 'delta_net_pct_oi', horizon: 4, lag: 4, method: 'pearson' },
  equity: { metric: 'delta_net', horizon: 4, lag: 1, method: 'pearson' },
  crypto: { metric: 'delta_net', horizon: 1, lag: 1, method: 'spearman' },
};

const getAssetClassForMarket = (market, pair) => {
  const name = `${market || ''}`.toUpperCase();
  const pairValue = `${pair || ''}`.toUpperCase();

  const isCrypto = /(BITCOIN|ETHER|ETH|SOL|XRP|DOGE|LITECOIN|CARDANO|AVALANCHE|CHAINLINK|HEDERA|SHIB|STELLAR|POLKADOT|CRYPTO|BTC|ADA|AVAX|LINK|HBAR|XLM)/.test(
    name
  );
  if (isCrypto) {
    return 'crypto';
  }

  const isRates = /(UST|U\\.S\\.|ULTRA|BOND|NOTE|SOFR|FED FUNDS|ERIS|YIELD|INT RATE|TREASURY)/.test(
    name
  );
  if (isRates) {
    return 'rates';
  }

  const isFx = pairValue.includes('/') || /USD INDEX/.test(name) || pairValue.includes('DXY');
  if (isFx) {
    return 'fx';
  }

  return 'equity';
};

const POSITIONING_TOUR_STEPS = [
  {
    key: 'intro',
    anchor: 'title',
    title: 'What this chart measures',
    listStyle: 'bullets',
    lines: [
      'This chart shows how changes in institutional positioning relate to future price moves. We use positioning changes (ΔNet), not raw levels, to avoid misleading conclusions.',
    ],
  },
  {
    key: 'metric',
    anchor: 'metric',
    title: 'Metric selection',
    lines: [
      'ΔNet shows whether institutions are adding or reducing exposure. ΔNet % OI normalizes this change by market size, allowing comparisons across time.',
    ],
    examples: {
      fx: 'ΔNet helps identify squeeze risk when leveraged funds rapidly change exposure.',
      rates: 'ΔNet % OI matters more as market size shifts with issuance and roll.',
      equities: 'ΔNet highlights risk-on vs risk-off reallocations by asset managers.',
    },
  },
  {
    key: 'horizon',
    anchor: 'horizon',
    title: 'Horizon (returns window)',
    lines: [
      'The horizon defines how far into the future returns are measured. Positioning typically impacts price over weeks, not days.',
    ],
    examples: {
      fx: 'FX: 1–2 weeks',
      rates: 'Rates: 4+ weeks',
      equities: 'Equities: 2–4 weeks',
    },
  },
  {
    key: 'leadlag',
    anchor: 'lag',
    title: 'Lead/Lag slider',
    lines: [
      'The Lead/Lag control shows who moves first. Positioning leading suggests flows may influence price. Price leading suggests positioning reacts.',
    ],
    examples: {
      fx: 'Positioning leading by 1–2 weeks often precedes short squeezes.',
      rates: 'Price frequently leads positioning as macro news hits first.',
      equities: 'Asset managers may lead during reallocations; fast money reacts.',
    },
  },
  {
    key: 'rho',
    anchor: 'rho',
    title: 'Correlation value & strength',
    lines: [
      'The correlation shows direction and strength, not timing. Weak correlations are common and still informative.',
    ],
  },
  {
    key: 'confidence',
    anchor: 'confidence',
    title: 'Confidence badge',
    lines: [
      'Confidence reflects sample size, stability across lags, and effect size — not trade accuracy.',
    ],
  },
  {
    key: 'curve',
    anchor: 'curve',
    title: 'Lead/Lag correlation curve',
    lines: [
      'This curve shows correlation across all lead/lag values. It helps prevent cherry-picking a single lag that looks favorable.',
    ],
  },
  {
    key: 'final',
    anchor: 'footer',
    title: 'Final takeaway',
    lines: ['Positioning explains market structure and flow pressure.'],
    boldLine: 'It tells you who may be forced next — not when to enter a trade.',
  },
];

const buildPositioningTourSteps = (assetClassKey) => {
  return POSITIONING_TOUR_STEPS.map((step) => {
    if (!step.examples) {
      return step;
    }
    const example = step.examples[assetClassKey] || step.examples.fx;
    return { ...step, example };
  });
};

const MARKET_TO_CURRENCY_PAIR = {
  'CANADIAN DOLLAR': 'CAD/USD',
  'SWISS FRANC': 'CHF/USD',
  'BRITISH POUND': 'GBP/USD',
  'JAPANESE YEN': 'JPY/USD',
  'EURO FX': 'EUR/USD',
  'EURO FX/BRITISH POUND XRATE': 'EUR/GBP',
  'EURO FX/JAPANESE YEN XRATE': 'EUR/JPY',
  'AUSTRALIAN DOLLAR': 'AUD/USD',
  'NZ DOLLAR': 'NZD/USD',
  'BRAZILIAN REAL': 'USD/BRL',
  'MEXICAN PESO': 'USD/MXN',
  'SO AFRICAN RAND': 'USD/ZAR',
  'USD INDEX': 'ICEUS:DXY1!',
};

const MARKET_TO_TRADINGVIEW_SYMBOL = {
  'E-MINI S&P 500': 'CME_MINI:ES1!',
  'MICRO E-MINI S&P 500 INDEX': 'CME_MINI:MES1!',
  'E-MINI NASDAQ-100': 'CME_MINI:NQ1!',
  'NASDAQ-100 CONSOLIDATED': 'NDX',
  'NASDAQ MINI': 'CME_MINI:NQ1!',
  'MICRO E-MINI NASDAQ-100 INDEX': 'CME_MINI:MNQ1!',
  'MICRO E-MINI DJIA (X$0.5)': 'CBOT_MINI:MYM1!',
  'DJIA X $5': 'CBOT_MINI:YM1!',
  'DJIA CONSOLIDATED': 'CBOT:DJIA',
  'DOW JONES U.S. REAL ESTATE IDX': 'CBOT:DJUSRE',
  'E-MINI RUSSELL 2000': 'CME_MINI:RTY1!',
  'MICRO E-MINI RUSSELL 2000 INDX': 'CME_MINI:M2K1!',
  'EMINI RUSSELL 1000 VALUE INDEX': 'CME_MINI:R1V1!',
  'EMINI RUSSELL 1000 GROWTH': 'CME_MINI:R1G1!',
  'RUSSELL 2000 ANNUAL DIVIDEND': 'CME:RTDIV',
  'NIKKEI STOCK AVERAGE': 'CME:NK1!',
  'NIKKEI STOCK AVERAGE YEN DENOM': 'CME:NK1!',
  'MSCI EAFE': 'ICE:MFSU1!',
  'MSCI EM INDEX': 'ICE:MFEM1!',
  'MINI MSCI ACWI NTR INDEX': 'ICE:ACWI1!',
  'S&P 500 ANNUAL DIVIDEND INDEX': 'CME:SPDIV',
  'S&P 500 QUARTERLY DIVIDEND IND': 'CME:SPQDIV',
  'ADJUSTED INT RATE S&P 500 TOTL': 'CME:ESIR',
  'UST BOND': 'CBOT:ZB1!',
  'ULTRA UST BOND': 'CBOT:UB1!',
  'ULTRA U.S. BOND': 'CBOT:UB1!',
  'UST 2Y NOTE': 'CBOT:ZT1!',
  '10 YEAR ERIS SOFR SWAP': 'CBOT:ZN1!',
  '5 YEAR ERIS SOFR SWAP': 'CBOT:ZF1!',
  '3 YEAR ERIS SOFR SWAP': 'CBOT:ZT1!',
  '2 YEAR ERIS SOFR SWAP': 'CBOT:ZT1!',
  'MICRO 10 YEAR YIELD': 'CBOT:ZN1!',
  'FED FUNDS': 'CBOT:ZQ1!',
  'SOFR-1M': 'CME:SR1!',
  'SOFR-3M': 'CME:SR3!',
  'UST 5Y NOTE': 'CBOT:ZF1!',
  'UST 10Y NOTE': 'CBOT:ZN1!',
  'ULTRA UST 10Y': 'CBOT:TN1!',
  'BITCOIN': 'CME:BTC1!',
  'ETHER CASH SETTLED': 'CME:ETH1!',
  '1K SHIB': 'BINANCE:SHIBUSDT',
  'AVALANCHE': 'BINANCE:AVAXUSDT',
  'AVALANCHE PERP STYLE': 'BINANCE:AVAXUSDT.P',
  'CARDONA': 'BINANCE:ADAUSDT',
  'CARDONA PERP STYLE': 'BINANCE:ADAUSDT.P',
  'CARDANO': 'BINANCE:ADAUSDT',
  'CARDANO PERP STYLE': 'BINANCE:ADAUSDT.P',
  'CHAINLINK': 'BINANCE:LINKUSDT',
  'CHAINLINK PERP STYLE': 'BINANCE:LINKUSDT.P',
  'DOGECOIN': 'BINANCE:DOGEUSDT',
  'LITECOIN CASH': 'BINANCE:LTCUSDT',
  'HEDERA': 'BINANCE:HBARUSDT',
  'EURO SHORT TERM RATE': 'CME:ESTR1!',
  'BBG COMMODITY': 'CBOT:BCOM',
  'MICRO BITCOIN': 'CME:MBT1!',
  'MICRO ETHER': 'CME:MET1!',
  'MICRO SOL': 'CME:MSL1!',
  'MICRO XRP': 'CME:MXP1!',
  'SOL': 'CME:SOL1!',
  'XRP': 'CME:XRP1!',
  'NANO BITCOIN': 'BINANCE:BTCUSDT',
  'NANO BITCOIN PERP STYLE': 'BINANCE:BTCUSDT.P',
  'NANO ETHER': 'BINANCE:ETHUSDT',
  'NANO ETHER PERP STYLE': 'BINANCE:ETHUSDT.P',
  'NANO SOLANA': 'BINANCE:SOLUSDT',
  'NANO SOLANA PERP STYLE': 'BINANCE:SOLUSDT.P',
  'NANO XRP': 'BINANCE:XRPUSDT',
  'NANO XRP PERP STYLE': 'BINANCE:XRPUSDT.P',
  'NANO STELLAR': 'BINANCE:XLMUSDT',
  'STELLAR': 'BINANCE:XLMUSDT',
  'POLKADOT': 'BINANCE:DOTUSDT',
  'VIX FUTURES': 'CFE:VX1!',
  'S&P 500 CONSOLIDATED': 'SPX',
  'E-MINI S&P CONSU STAPLES INDEX': 'CME_MINI:XPS1!',
  'E-MINI S&P ENERGY INDEX': 'CME_MINI:XES1!',
  'E-MINI S&P FINANCIAL INDEX': 'CME_MINI:XAF1!',
  'E-MINI S&P HEALTH CARE INDEX': 'CME_MINI:XAV1!',
  'E-MINI S&P INDUSTRIAL INDEX': 'CME_MINI:XAI1!',
  'E-MINI S&P MATERIALS INDEX': 'CME_MINI:XLB1!',
  'E-MINI S&P TECHNOLOGY INDEX': 'CME_MINI:XLK1!',
  'E-MINI S&P UTILITIES INDEX': 'CME_MINI:XLU1!',
  'E-MINI S&P COMMUNICATION INDEX': 'CME_MINI:XLC1!',
  'E-MINI S&P REAL ESTATE INDEX': 'CME_MINI:XLRE1!',
  'E-MINI S&P 400 STOCK INDEX': 'CME_MINI:EMD1!',
};

const deriveCurrencyPairFromMarket = (marketName) => {
  if (!marketName) {
    return null;
  }
  const normalized = marketName.trim().toUpperCase();
  const directMatch = normalized.split('-')[0].trim();
  if (MARKET_TO_CURRENCY_PAIR[directMatch]) {
    return MARKET_TO_CURRENCY_PAIR[directMatch];
  }

  for (const [key, pair] of Object.entries(MARKET_TO_CURRENCY_PAIR)) {
    if (normalized.includes(key)) {
      return pair;
    }
  }

  return null;
};

const deriveTradingViewSymbolFromMarket = (marketName) => {
  if (!marketName) {
    return null;
  }
  const normalized = marketName.trim().toUpperCase();
  const directMatch = normalized.split('-')[0].trim();
  if (MARKET_TO_TRADINGVIEW_SYMBOL[directMatch]) {
    return MARKET_TO_TRADINGVIEW_SYMBOL[directMatch];
  }
  for (const [key, symbol] of Object.entries(MARKET_TO_TRADINGVIEW_SYMBOL)) {
    if (normalized.includes(key)) {
      return symbol;
    }
  }
  return null;
};

const parseDateString = (value) => {
  const date = moment(value);
  return date.isValid() ? date.toDate() : null;
};

const toTradingViewSymbol = (pair, source) => {
  if (!pair) {
    return null;
  }
  const normalized = pair.replace('/', '').toUpperCase();
  const prefix = source || 'OANDA';
  return `${prefix}:${normalized}`;
};

const loadTradingViewScript = () => {
  if (window.TradingView) {
    return Promise.resolve();
  }
  if (document.getElementById('tradingview-widget-script')) {
    return new Promise((resolve) => {
      const existing = document.getElementById('tradingview-widget-script');
      existing.addEventListener('load', resolve, { once: true });
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = 'tradingview-widget-script';
    script.src = 'https://s3.tradingview.com/tv.js';
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });
};

function SideDrawer() {
  const { timeInterval, setTimeInterval, cftcSubSection, setCftcSubSection } =
    useAppContext();
  const isImpactReportView = cftcSubSection === 'impact-report';
  const trendMode =
    cftcSubSection === 'trend' ||
    cftcSubSection === 'long-trend' ||
    cftcSubSection === 'short-trend'
      ? 'trend'
      : null;
  const isTrendView = Boolean(trendMode);
  const markets = useMemo(() => {
    const unique = new Set(sampleData.map((row) => row.Market_and_Exchange_Names || ''));
    return Array.from(unique).filter(Boolean).sort();
  }, []);
  const [selectedMarket, setSelectedMarket] = useState(markets[0] || '');
  const [selectedReportDate, setSelectedReportDate] = useState(
    () => sampleData[0]?.['Report_Date_as_YYYY-MM-DD'] || ''
  );
  const [selectedReportYear, setSelectedReportYear] = useState('');
  const [selectedReportMonth, setSelectedReportMonth] = useState('');
  const currencyPair = useMemo(() => deriveCurrencyPairFromMarket(selectedMarket), [selectedMarket]);
  const lastMarketRef = useRef('');
  const lastTrendMarketRef = useRef('');
  const correlationTitleRef = useRef(null);
  const correlationMetricRef = useRef(null);
  const correlationHorizonRef = useRef(null);
  const correlationLagRef = useRef(null);
  const correlationRhoRef = useRef(null);
  const correlationConfidenceRef = useRef(null);
  const correlationSparklineRef = useRef(null);
  const correlationFooterRef = useRef(null);
  const [trendStartYear, setTrendStartYear] = useState('');
  const [trendStartMonth, setTrendStartMonth] = useState('');
  const [trendEndYear, setTrendEndYear] = useState('');
  const [trendEndMonth, setTrendEndMonth] = useState('');
  const [snapshotTab, setSnapshotTab] = useState('summary');
  const [positionView, setPositionView] = useState('net');
  const [correlationMetric, setCorrelationMetric] = useState('delta_net');
  const [correlationHorizon, setCorrelationHorizon] = useState(2);
  const [correlationLag, setCorrelationLag] = useState(2);
  const [correlationMethod, setCorrelationMethod] = useState('pearson');
  const [showCorrelationMethodology, setShowCorrelationMethodology] = useState(false);
  const [showRhoHelpModal, setShowRhoHelpModal] = useState(false);
  const [rhoHelpTab, setRhoHelpTab] = useState('rho');
  const [alertsPanelOpen, setAlertsPanelOpen] = useState(false);
  const [trendSettingsOpen, setTrendSettingsOpen] = useState(false);
  const trendSettingsRef = useRef(null);
  const [correlationSettingsOpen, setCorrelationSettingsOpen] = useState(false);
  const correlationSettingsRef = useRef(null);
  const correlationSettingsAutoRef = useRef(false);
  const [alertsEnabledBySymbol, setAlertsEnabledBySymbol] = useState(() => {
    if (typeof window === 'undefined') {
      return {};
    }
    try {
      return JSON.parse(window.localStorage.getItem('cftcAlertsEnabledBySymbol') || '{}');
    } catch (err) {
      return {};
    }
  });
  const alertsEnabled = alertsEnabledBySymbol[selectedMarket] ?? true;
  const { mode: priceReferenceMode, setMode: setPriceReferenceMode } = usePriceReferenceMode();
  const [showPositioningTour, setShowPositioningTour] = useState(false);
  const [positioningTourStep, setPositioningTourStep] = useState(0);
  const [positioningTourPosition, setPositioningTourPosition] = useState(null);
  const [hasSeenPositioningTour, setHasSeenPositioningTour] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    try {
      return window.localStorage.getItem('hasSeenPositioningTour') === 'true';
    } catch (err) {
      return false;
    }
  });
  const [signalsOnboarding, setSignalsOnboarding] = useState(() => {
    if (typeof window === 'undefined') {
      return createSignalsOnboardingState(false);
    }
    return createSignalsOnboardingState(readSignalsOnboardingSeen(window.localStorage));
  });
  const signalsOnboardingStorage =
    typeof window === 'undefined' ? null : window.localStorage;
  const [cftcSummaryOnboarding, setCftcSummaryOnboarding] = useState(() => {
    if (typeof window === 'undefined') {
      return createCftcSummaryOnboardingState(false);
    }
    return createCftcSummaryOnboardingState(readCftcSummaryOnboardingSeen(window.localStorage));
  });
  const cftcSummaryOnboardingStorage =
    typeof window === 'undefined' ? null : window.localStorage;
  const [positionTrendsOnboarding, setPositionTrendsOnboarding] = useState(() => {
    if (typeof window === 'undefined') {
      return createPositionTrendsOnboardingState(false);
    }
    return createPositionTrendsOnboardingState(
      readPositionTrendsOnboardingSeen(window.localStorage),
    );
  });
  const positionTrendsOnboardingStorage =
    typeof window === 'undefined' ? null : window.localStorage;
  const [grossActivityBadgeEnabled, setGrossActivityBadgeEnabled] = useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }
    try {
      const stored = window.localStorage.getItem('ui.grossActivityBadge.enabled');
      return stored == null ? true : stored === 'true';
    } catch (err) {
      return true;
    }
  });
  const correlationTouchedRef = useRef(false);
  const lastCorrelationMarketRef = useRef('');
  const [showOpenInterest, setShowOpenInterest] = useState(true);
  const [showWeeklyMarkers, setShowWeeklyMarkers] = useState(false);
  const [extremesMode, setExtremesMode] = useState('1y');
  const [extremesTouched, setExtremesTouched] = useState(false);
  const [assetChartData, setAssetChartData] = useState(null);
  const [assetLoading, setAssetLoading] = useState(false);
  const [assetError, setAssetError] = useState('');
  const [snapshotIntervalApplied, setSnapshotIntervalApplied] = useState(false);
  const [trendPriceData, setTrendPriceData] = useState([]);
  const [trendPriceMeta, setTrendPriceMeta] = useState({ pair: '', rangeKey: '', interval: '' });
  const trendPriceRequestRef = useRef(0);
  const tvContainerId = useRef(`tradingview-${Math.random().toString(36).slice(2, 10)}`);
  const [tvSourceByMarket, setTvSourceByMarket] = useState(() => {
    if (typeof window === 'undefined') {
      return {};
    }
    try {
      return JSON.parse(window.localStorage.getItem('cftcTvSourceByMarket') || '{}');
    } catch (err) {
      return {};
    }
  });
  const [tvIntervalByMarket, setTvIntervalByMarket] = useState(() => {
    if (typeof window === 'undefined') {
      return {};
    }
    try {
      return JSON.parse(window.localStorage.getItem('cftcTvIntervalByMarket') || '{}');
    } catch (err) {
      return {};
    }
  });
  const [tvRangeByMarket, setTvRangeByMarket] = useState(() => {
    if (typeof window === 'undefined') {
      return {};
    }
    try {
      return JSON.parse(window.localStorage.getItem('cftcTvRangeByMarket') || '{}');
    } catch (err) {
      return {};
    }
  });
  const [tvSymbolOverrideByMarket, setTvSymbolOverrideByMarket] = useState(() => {
    if (typeof window === 'undefined') {
      return {};
    }
    try {
      return JSON.parse(window.localStorage.getItem('cftcTvSymbolOverrides') || '{}');
    } catch (err) {
      return {};
    }
  });
  const [tvRangeDefault] = useState(() => {
    if (typeof window === 'undefined') {
      return '6M';
    }
    return window.localStorage.getItem('cftcTvRangeDefault') || '6M';
  });
  const marketGroupInitRef = useRef(false);
  const positionViewLabel = useMemo(() => {
    const match = POSITION_VIEW_OPTIONS.find((option) => option.value === positionView);
    return match ? match.label : 'Net';
  }, [positionView]);
  const tvSource = tvSourceByMarket[selectedMarket] || TRADINGVIEW_SOURCES[0];
  const tvInterval =
    tvIntervalByMarket[selectedMarket] || TRADINGVIEW_INTERVALS[0]?.value || 'D';
  const tvRange = tvRangeByMarket[selectedMarket] || tvRangeDefault;
  const tvSymbolDerived = useMemo(() => {
    const mapped = deriveTradingViewSymbolFromMarket(selectedMarket);
    if (mapped) {
      return mapped;
    }
    return toTradingViewSymbol(currencyPair, tvSource);
  }, [selectedMarket, currencyPair, tvSource]);
  const tvSymbol = tvSymbolOverrideByMarket[selectedMarket] ?? tvSymbolDerived;
  const [tickerEditMap, setTickerEditMap] = useState({});
  const assetClassKey = useMemo(() => {
    const detected = getAssetClassForMarket(selectedMarket, currencyPair);
    if (detected === 'equity') {
      return 'equities';
    }
    if (detected === 'crypto') {
      return 'fx';
    }
    return detected;
  }, [selectedMarket, currencyPair]);
  const rulesAssetClass = useMemo(() => {
    if (assetClassKey === 'rates') {
      return 'Rates';
    }
    if (assetClassKey === 'equities') {
      return 'Equities';
    }
    return 'FX';
  }, [assetClassKey]);
  const rulesAssetConfig = useMemo(() => {
    return cftcRules.assetClasses?.[rulesAssetClass] || cftcRules.assetClasses?.FX;
  }, [rulesAssetClass]);
  const readinessScale = useMemo(() => {
    return cftcRules.readinessScore?.labels || [];
  }, []);
  const readinessDefinitions = useMemo(() => {
    if (!rulesAssetConfig) {
      return null;
    }
    const thresholds = rulesAssetConfig.thresholds || {};
    const weights = rulesAssetConfig.readinessWeights || {};
    const lfExtreme = thresholds.lfExtremePct ?? '—';
    const lfWatch = thresholds.lfWatchPct ?? '—';
    const oiPositive = thresholds.oiChange4wPositive ?? '—';
    const breakoutLookback = thresholds.breakoutLookbackDays ?? '—';
    const maDays = thresholds.maDays ?? '—';
    return [
      {
        title: `Offsides (max ${weights.offsides ?? 0})`,
        items: [
          `LF net percentile <= ${lfExtreme}: full score`,
          `LF net percentile <= ${lfWatch}: 75% score`,
          'LF net percentile <= 25: 37.5% score',
        ],
      },
      {
        title: `Participation (max ${weights.participation ?? 0})`,
        items: [
          `OI change 4w > ${oiPositive}: full score`,
          'OI change 4w >= -0.01: 50% score',
        ],
      },
      {
        title: `Pressure (max ${weights.pressure ?? 0})`,
        items: ['LF delta net 4w < 0: 50% score', 'Price return 4w <= 0: 50% score'],
      },
      {
        title: `Trigger (max ${weights.trigger ?? 0})`,
        items: [
          `Breakout over ${breakoutLookback}d: 75% score`,
          `Close above ${maDays}d MA: 25% score`,
        ],
      },
    ];
  }, [rulesAssetConfig]);
  const positioningTourSteps = useMemo(() => {
    return buildPositioningTourSteps(assetClassKey);
  }, [assetClassKey]);
  const signalsOnboardingSteps = useMemo(() => {
    return buildSignalsOnboardingSteps(rulesAssetClass);
  }, [rulesAssetClass]);
  const cftcSummaryOnboardingSteps = useMemo(() => {
    return buildCftcSummaryOnboardingSteps();
  }, []);
  const positionTrendsOnboardingSteps = useMemo(() => {
    return buildPositionTrendsOnboardingSteps();
  }, []);
  const activeTourStep = showPositioningTour
    ? positioningTourSteps[positioningTourStep]
    : null;
  const handlePositioningTourAdvance = () => {
    if (!activeTourStep) {
      return;
    }
    if (positioningTourStep >= positioningTourSteps.length - 1) {
      setShowPositioningTour(false);
      setHasSeenPositioningTour(true);
      try {
        window.localStorage.setItem('hasSeenPositioningTour', 'true');
      } catch (err) {
        // Ignore storage failures.
      }
      return;
    }
    setPositioningTourStep((prev) => prev + 1);
  };
  const handlePositioningTourBack = () => {
    setPositioningTourStep((prev) => Math.max(0, prev - 1));
  };
  const handlePositioningTourSkip = () => {
    setShowPositioningTour(false);
    setHasSeenPositioningTour(true);
    try {
      window.localStorage.setItem('hasSeenPositioningTour', 'true');
    } catch (err) {
      // Ignore storage failures.
    }
  };
  const handlePositioningTourRestart = () => {
    setShowPositioningTour(true);
    setPositioningTourStep(0);
  };
  const handleSignalsOnboardingNext = () => {
    if (signalsOnboarding.step >= signalsOnboardingSteps.length - 1) {
      setSignalsOnboarding((prev) => getDoneSignalsOnboardingState(prev));
      writeSignalsOnboardingSeen(signalsOnboardingStorage, true);
      return;
    }
    setSignalsOnboarding((prev) => ({ ...prev, step: prev.step + 1 }));
  };
  const handleSignalsOnboardingBack = () => {
    setSignalsOnboarding((prev) => ({ ...prev, step: Math.max(0, prev.step - 1) }));
  };
  const handleSignalsOnboardingSkip = () => {
    setSignalsOnboarding((prev) => getSkipSignalsOnboardingState(prev));
    writeSignalsOnboardingSeen(signalsOnboardingStorage, true);
  };
  const handleSignalsOnboardingDone = () => {
    setSignalsOnboarding((prev) => getDoneSignalsOnboardingState(prev));
    writeSignalsOnboardingSeen(signalsOnboardingStorage, true);
  };
  const handleSignalsOnboardingClose = () => {
    setSignalsOnboarding((prev) => getCloseSignalsOnboardingState(prev));
  };
  const handleSignalsOnboardingReplay = () => {
    setSignalsOnboarding((prev) => getReplaySignalsOnboardingState(prev));
  };
  const handleCftcSummaryOnboardingNext = () => {
    if (cftcSummaryOnboarding.step >= cftcSummaryOnboardingSteps.length - 1) {
      setCftcSummaryOnboarding((prev) => getDoneCftcSummaryOnboardingState(prev));
      writeCftcSummaryOnboardingSeen(cftcSummaryOnboardingStorage, true);
      return;
    }
    setCftcSummaryOnboarding((prev) => ({ ...prev, step: prev.step + 1 }));
  };
  const handleCftcSummaryOnboardingBack = () => {
    setCftcSummaryOnboarding((prev) => ({ ...prev, step: Math.max(0, prev.step - 1) }));
  };
  const handleCftcSummaryOnboardingSkip = () => {
    setCftcSummaryOnboarding((prev) => getSkipCftcSummaryOnboardingState(prev));
    writeCftcSummaryOnboardingSeen(cftcSummaryOnboardingStorage, true);
  };
  const handleCftcSummaryOnboardingDone = () => {
    setCftcSummaryOnboarding((prev) => getDoneCftcSummaryOnboardingState(prev));
    writeCftcSummaryOnboardingSeen(cftcSummaryOnboardingStorage, true);
  };
  const handleCftcSummaryOnboardingClose = () => {
    setCftcSummaryOnboarding((prev) => getCloseCftcSummaryOnboardingState(prev));
  };
  const handleCftcSummaryOnboardingReplay = () => {
    setCftcSummaryOnboarding((prev) => getReplayCftcSummaryOnboardingState(prev));
  };
  const handlePositionTrendsOnboardingNext = () => {
    if (positionTrendsOnboarding.step >= positionTrendsOnboardingSteps.length - 1) {
      setPositionTrendsOnboarding((prev) => getDonePositionTrendsOnboardingState(prev));
      writePositionTrendsOnboardingSeen(positionTrendsOnboardingStorage, true);
      return;
    }
    setPositionTrendsOnboarding((prev) => ({ ...prev, step: prev.step + 1 }));
  };
  const handlePositionTrendsOnboardingBack = () => {
    setPositionTrendsOnboarding((prev) => ({ ...prev, step: Math.max(0, prev.step - 1) }));
  };
  const handlePositionTrendsOnboardingSkip = () => {
    setPositionTrendsOnboarding((prev) => getSkipPositionTrendsOnboardingState(prev));
    writePositionTrendsOnboardingSeen(positionTrendsOnboardingStorage, true);
  };
  const handlePositionTrendsOnboardingDone = () => {
    setPositionTrendsOnboarding((prev) => getDonePositionTrendsOnboardingState(prev));
    writePositionTrendsOnboardingSeen(positionTrendsOnboardingStorage, true);
  };
  const handlePositionTrendsOnboardingClose = () => {
    setPositionTrendsOnboarding((prev) => getClosePositionTrendsOnboardingState(prev));
  };
  const handlePositionTrendsOnboardingReplay = () => {
    setPositionTrendsOnboarding((prev) => getReplayPositionTrendsOnboardingState(prev));
  };
  const contractGroups = useMemo(() => {
    const sp500Label = 'S&P 500 – CORE & SECTOR E-MINIS (CME)';
    const sp500Matchers = [
      /^S&P 500/i,
      /^E-MINI S&P/i,
      /^E-MINI S&P 400/i,
      /^E-MINI S&P COMMUNICATION/i,
    ];
    const sp500Set = new Set();
    markets.forEach((market) => {
      if (sp500Matchers.some((matcher) => matcher.test(market))) {
        sp500Set.add(market);
      }
    });
    const sp500Options = markets.filter((market) => sp500Set.has(market));
    const dowLabel = 'DOW JONES / CBOT';
    const dowMatchers = [
      /^DJIA CONSOLIDATED/i,
      /^DJIA X \\$5/i,
      /^MICRO E-MINI DJIA/i,
      /^DOW JONES U\\.S\\. REAL ESTATE IDX/i,
    ];
    const dowSet = new Set();
    markets.forEach((market) => {
      if (dowMatchers.some((matcher) => matcher.test(market))) {
        dowSet.add(market);
      }
    });
    const dowOptions = markets.filter((market) => dowSet.has(market));
    const nasdaqLabel = 'NASDAQ (CME)';
    const nasdaqMatchers = [
      /^NASDAQ-100 CONSOLIDATED/i,
      /^NASDAQ MINI/i,
      /^MICRO E-MINI NASDAQ-100/i,
    ];
    const nasdaqSet = new Set();
    markets.forEach((market) => {
      if (nasdaqMatchers.some((matcher) => matcher.test(market))) {
        nasdaqSet.add(market);
      }
    });
    const nasdaqOptions = markets.filter((market) => nasdaqSet.has(market));
    const russellLabel = 'RUSSELL (CME)';
    const russellMatchers = [
      /^RUSSELL E-MINI/i,
      /^MICRO E-MINI RUSSELL 2000/i,
      /^EMINI RUSSELL 1000 VALUE/i,
      /^EMINI RUSSELL 1000 GROWTH/i,
      /^RUSSELL 2000 ANNUAL DIVIDEND/i,
    ];
    const russellSet = new Set();
    markets.forEach((market) => {
      if (russellMatchers.some((matcher) => matcher.test(market))) {
        russellSet.add(market);
      }
    });
    const russellOptions = markets.filter((market) => russellSet.has(market));
    const internationalLabel = 'INTERNATIONAL EQUITY FUTURES';
    const internationalMatchers = [
      /^NIKKEI STOCK AVERAGE/i,
      /^MSCI EAFE/i,
      /^MSCI EM INDEX/i,
      /^MINI MSCI ACWI NTR INDEX/i,
    ];
    const internationalSet = new Set();
    markets.forEach((market) => {
      if (internationalMatchers.some((matcher) => matcher.test(market))) {
        internationalSet.add(market);
      }
    });
    const internationalOptions = markets.filter((market) => internationalSet.has(market));
    const dividendLabel = 'DIVIDEND & RATE-ADJUSTED INDICES';
    const dividendMatchers = [
      /^S&P 500 ANNUAL DIVIDEND INDEX/i,
      /^S&P 500 QUARTERLY DIVIDEND IND/i,
      /^ADJUSTED INT RATE S&P 500 TOTL/i,
    ];
    const dividendSet = new Set();
    markets.forEach((market) => {
      if (dividendMatchers.some((matcher) => matcher.test(market))) {
        dividendSet.add(market);
      }
    });
    const dividendOptions = markets.filter((market) => dividendSet.has(market));
    const treasuryLabel = 'U.S. TREASURY FUTURES — CBOT';
    const treasuryMatchers = [
      /^UST BOND/i,
      /^ULTRA UST BOND/i,
      /^UST 2Y NOTE/i,
    ];
    const treasurySet = new Set();
    markets.forEach((market) => {
      if (treasuryMatchers.some((matcher) => matcher.test(market))) {
        treasurySet.add(market);
      }
    });
    const treasuryOptions = markets.filter((market) => treasurySet.has(market));
    const ratesLabel = 'INTEREST RATES / SWAPS (CBOT / ERIS)';
    const ratesMatchers = [
      /^10 YEAR ERIS SOFR SWAP/i,
      /^5 YEAR ERIS SOFR SWAP/i,
      /^3 YEAR ERIS SOFR SWAP/i,
      /^2 YEAR ERIS SOFR SWAP/i,
      /^MICRO 10 YEAR YIELD/i,
      /^FED FUNDS/i,
    ];
    const ratesSet = new Set();
    markets.forEach((market) => {
      if (ratesMatchers.some((matcher) => matcher.test(market))) {
        ratesSet.add(market);
      }
    });
    const ratesOptions = markets.filter((market) => ratesSet.has(market));
    const cryptoLabel = 'CME CRYPTO FUTURES';
    const cryptoMatchers = [
      /^BITCOIN/i,
      /^ETHER CASH SETTLED/i,
    ];
    const cryptoSet = new Set();
    markets.forEach((market) => {
      if (cryptoMatchers.some((matcher) => matcher.test(market))) {
        cryptoSet.add(market);
      }
    });
    const cryptoOptions = markets.filter((market) => cryptoSet.has(market));
    const perpLabel = 'COINBASE / LMX PERPETUALS';
    const perpMatchers = [
      /^1K SHIB/i,
      /^AVALANCHE/i,
      /^CARDONA/i,
      /^CARDANO/i,
      /^CHAINLINK/i,
      /^DOGECOIN/i,
      /^LITECOIN CASH/i,
      /^HEDERA/i,
    ];
    const perpSet = new Set();
    markets.forEach((market) => {
      if (perpMatchers.some((matcher) => matcher.test(market))) {
        perpSet.add(market);
      }
    });
    const perpOptions = markets.filter((market) => perpSet.has(market));
    const nanoLabel = 'CRYPTO – COINBASE / LMX';
    const nanoMatchers = [
      /^NANO BITCOIN/i,
      /^NANO ETHER/i,
      /^NANO SOLANA/i,
      /^NANO XRP/i,
      /^NANO STELLAR/i,
      /^STELLAR/i,
      /^POLKADOT/i,
    ];
    const nanoSet = new Set();
    markets.forEach((market) => {
      if (nanoMatchers.some((matcher) => matcher.test(market))) {
        nanoSet.add(market);
      }
    });
    const nanoOptions = markets.filter((market) => nanoSet.has(market));
    const equityIndexLabel = 'EQUITY INDEX FUTURES — CBOT / CME';
    const equityIndexMatchers = [
      /^DJIA X \\$5/i,
      /^DOW JONES U\\.S\\. REAL ESTATE IDX/i,
      /^EURO SHORT TERM RATE/i,
      /^BBG COMMODITY/i,
    ];
    const equityIndexSet = new Set();
    markets.forEach((market) => {
      if (equityIndexMatchers.some((matcher) => matcher.test(market))) {
        equityIndexSet.add(market);
      }
    });
    const equityIndexOptions = markets.filter((market) => equityIndexSet.has(market));
    const cryptoCmeLabel = 'CRYPTO FUTURES';
    const cryptoCmeMatchers = [
      /^MICRO BITCOIN/i,
      /^MICRO ETHER/i,
      /^MICRO SOL/i,
      /^MICRO XRP/i,
      /^SOL$/i,
      /^XRP$/i,
    ];
    const cryptoCmeSet = new Set();
    markets.forEach((market) => {
      if (cryptoCmeMatchers.some((matcher) => matcher.test(market))) {
        cryptoCmeSet.add(market);
      }
    });
    const cryptoCmeOptions = markets.filter((market) => cryptoCmeSet.has(market));
    const usdVolLabel = 'USD / VOLATILITY / INDEXES';
    const usdVolMatchers = [
      /^USD INDEX/i,
      /^VIX FUTURES/i,
    ];
    const usdVolSet = new Set();
    markets.forEach((market) => {
      if (usdVolMatchers.some((matcher) => matcher.test(market))) {
        usdVolSet.add(market);
      }
    });
    const usdVolOptions = markets.filter((market) => usdVolSet.has(market));
    const equityCmeLabel = 'EQUITY INDEX – CME';
    const equityCmeMatchers = [
      /^MICRO E-MINI S&P 500/i,
    ];
    const equityCmeSet = new Set();
    markets.forEach((market) => {
      if (equityCmeMatchers.some((matcher) => matcher.test(market))) {
        equityCmeSet.add(market);
      }
    });
    const equityCmeOptions = markets.filter((market) => equityCmeSet.has(market));
    const fxLabel = 'FX FUTURES – CME';
    const fxMatchers = [
      /^CANADIAN DOLLAR/i,
      /^SWISS FRANC/i,
      /^BRITISH POUND/i,
      /^JAPANESE YEN/i,
      /^EURO FX/i,
      /^AUSTRALIAN DOLLAR/i,
      /^NZ DOLLAR/i,
      /^BRAZILIAN REAL/i,
      /^MEXICAN PESO/i,
      /^SO AFRICAN RAND/i,
    ];
    const fxSet = new Set();
    markets.forEach((market) => {
      if (fxMatchers.some((matcher) => matcher.test(market))) {
        fxSet.add(market);
      }
    });
    const fxOptions = markets.filter((market) => fxSet.has(market));
    const interestLabel = 'INTEREST RATES – CME / CBOT';
    const interestMatchers = [
      /^SOFR-1M/i,
      /^SOFR-3M/i,
      /^UST 5Y NOTE/i,
      /^UST 10Y NOTE/i,
      /^ULTRA UST 10Y/i,
      /^ULTRA UST BOND/i,
    ];
    const interestSet = new Set();
    markets.forEach((market) => {
      if (interestMatchers.some((matcher) => matcher.test(market))) {
        interestSet.add(market);
      }
    });
    const interestOptions = markets.filter((market) => interestSet.has(market));
    const otherOptions = markets.filter(
      (market) =>
        !sp500Set.has(market) &&
        !dowSet.has(market) &&
        !nasdaqSet.has(market) &&
        !russellSet.has(market) &&
        !internationalSet.has(market) &&
        !dividendSet.has(market) &&
        !treasurySet.has(market) &&
        !ratesSet.has(market) &&
        !cryptoSet.has(market) &&
        !perpSet.has(market) &&
        !nanoSet.has(market) &&
        !equityIndexSet.has(market) &&
        !cryptoCmeSet.has(market) &&
        !interestSet.has(market) &&
        !usdVolSet.has(market) &&
        !equityCmeSet.has(market) &&
        !fxSet.has(market)
    );
    return [
      sp500Options.length ? { label: sp500Label, options: sp500Options } : null,
      dowOptions.length ? { label: dowLabel, options: dowOptions } : null,
      nasdaqOptions.length ? { label: nasdaqLabel, options: nasdaqOptions } : null,
      russellOptions.length ? { label: russellLabel, options: russellOptions } : null,
      internationalOptions.length
        ? { label: internationalLabel, options: internationalOptions }
        : null,
      dividendOptions.length ? { label: dividendLabel, options: dividendOptions } : null,
      treasuryOptions.length ? { label: treasuryLabel, options: treasuryOptions } : null,
      ratesOptions.length ? { label: ratesLabel, options: ratesOptions } : null,
      cryptoOptions.length ? { label: cryptoLabel, options: cryptoOptions } : null,
      perpOptions.length ? { label: perpLabel, options: perpOptions } : null,
      nanoOptions.length ? { label: nanoLabel, options: nanoOptions } : null,
      equityIndexOptions.length ? { label: equityIndexLabel, options: equityIndexOptions } : null,
      cryptoCmeOptions.length ? { label: cryptoCmeLabel, options: cryptoCmeOptions } : null,
      interestOptions.length ? { label: interestLabel, options: interestOptions } : null,
      usdVolOptions.length ? { label: usdVolLabel, options: usdVolOptions } : null,
      equityCmeOptions.length ? { label: equityCmeLabel, options: equityCmeOptions } : null,
      fxOptions.length ? { label: fxLabel, options: fxOptions } : null,
      otherOptions.length ? { label: 'All Contracts', options: otherOptions } : null,
    ].filter(Boolean);
  }, [markets]);
  const marketGroupMap = useMemo(() => {
    const map = new Map();
    contractGroups.forEach((group) => {
      group.options.forEach((market) => map.set(market, group.label));
    });
    return map;
  }, [contractGroups]);
  const fxGroupLabel = 'FX FUTURES – CME';
  const [selectedMarketGroup, setSelectedMarketGroup] = useState(() => {
    const fxGroup = contractGroups.find((group) => group.label === fxGroupLabel);
    return fxGroup ? fxGroup.label : contractGroups[0]?.label || '';
  });
  const selectedGroupOptions = useMemo(() => {
    const group = contractGroups.find((entry) => entry.label === selectedMarketGroup);
    return group ? group.options : [];
  }, [contractGroups, selectedMarketGroup]);
  const [impactReportScopeDraft, setImpactReportScopeDraft] = useState(
    'selected_market_group'
  );
  const [impactReportLookbackDraft, setImpactReportLookbackDraft] = useState(4);
  const [impactReportGroupIdsDraft, setImpactReportGroupIdsDraft] = useState(
    DEFAULT_IMPACT_REPORT_GROUP_IDS
  );
  const [impactReportRequest, setImpactReportRequest] = useState(() => ({
    scope: 'selected_market_group',
    lookbackWeeks: 4,
    groupIds: DEFAULT_IMPACT_REPORT_GROUP_IDS,
  }));
  const [impactReportGeneratedAt, setImpactReportGeneratedAt] = useState(() => Date.now());

  useEffect(() => {
    if (!selectedGroupOptions.length) {
      return;
    }
    if (selectedGroupOptions.includes(selectedMarket)) {
      return;
    }
    setSelectedMarket(selectedGroupOptions[0]);
  }, [selectedGroupOptions, selectedMarket]);

  const filteredByMarket = useMemo(() => {
    const filtered = sampleData.filter(
      (row) => row.Market_and_Exchange_Names === selectedMarket
    );
    return filtered.sort((a, b) => {
      const dateA = new Date(b['Report_Date_as_YYYY-MM-DD']);
      const dateB = new Date(a['Report_Date_as_YYYY-MM-DD']);
      return dateA - dateB;
    });
  }, [selectedMarket]);
  const reportDateOptions = useMemo(() => {
    return filteredByMarket.slice().sort((a, b) => {
      const dateA = new Date(
        a['Report_Date_as_YYYY-MM-DD'] || a.As_of_Date_In_Form_YYMMDD || 0
      );
      const dateB = new Date(
        b['Report_Date_as_YYYY-MM-DD'] || b.As_of_Date_In_Form_YYMMDD || 0
      );
      return dateB - dateA;
    });
  }, [filteredByMarket]);
  const reportYearOptions = useMemo(() => {
    const years = new Set();
    reportDateOptions.forEach((row) => {
      const value =
        row['Report_Date_as_YYYY-MM-DD'] || row.As_of_Date_In_Form_YYMMDD || '';
      const date = moment(value, ['YYYY-MM-DD', 'YYMMDD'], true);
      if (date.isValid()) {
        years.add(date.format('YYYY'));
      }
    });
    return Array.from(years).sort((a, b) => Number(b) - Number(a));
  }, [reportDateOptions]);
  const reportMonthOptions = useMemo(() => {
    if (!selectedReportYear) {
      return [];
    }
    const months = new Set();
    reportDateOptions.forEach((row) => {
      const value =
        row['Report_Date_as_YYYY-MM-DD'] || row.As_of_Date_In_Form_YYMMDD || '';
      const date = moment(value, ['YYYY-MM-DD', 'YYMMDD'], true);
      if (date.isValid() && date.format('YYYY') === selectedReportYear) {
        months.add(date.format('MM'));
      }
    });
    return Array.from(months).sort((a, b) => Number(b) - Number(a));
  }, [reportDateOptions, selectedReportYear]);
  const trendYearOptions = reportYearOptions;
  const trendStartMonthOptions = useMemo(() => {
    if (!trendStartYear) {
      return [];
    }
    const months = new Set();
    reportDateOptions.forEach((row) => {
      const value =
        row['Report_Date_as_YYYY-MM-DD'] || row.As_of_Date_In_Form_YYMMDD || '';
      const date = moment(value, ['YYYY-MM-DD', 'YYMMDD'], true);
      if (date.isValid() && date.format('YYYY') === trendStartYear) {
        months.add(date.format('MM'));
      }
    });
    return Array.from(months).sort((a, b) => Number(a) - Number(b));
  }, [reportDateOptions, trendStartYear]);
  const trendEndMonthOptions = useMemo(() => {
    if (!trendEndYear) {
      return [];
    }
    const months = new Set();
    reportDateOptions.forEach((row) => {
      const value =
        row['Report_Date_as_YYYY-MM-DD'] || row.As_of_Date_In_Form_YYMMDD || '';
      const date = moment(value, ['YYYY-MM-DD', 'YYMMDD'], true);
      if (date.isValid() && date.format('YYYY') === trendEndYear) {
        months.add(date.format('MM'));
      }
    });
    return Array.from(months).sort((a, b) => Number(a) - Number(b));
  }, [reportDateOptions, trendEndYear]);
  const reportDateFilteredOptions = useMemo(() => {
    if (!selectedReportYear || !selectedReportMonth) {
      return reportDateOptions;
    }
    return reportDateOptions.filter((row) => {
      const value =
        row['Report_Date_as_YYYY-MM-DD'] || row.As_of_Date_In_Form_YYMMDD || '';
      const date = moment(value, ['YYYY-MM-DD', 'YYMMDD'], true);
      return (
        date.isValid() &&
        date.format('YYYY') === selectedReportYear &&
        date.format('MM') === selectedReportMonth
      );
    });
  }, [reportDateOptions, selectedReportMonth, selectedReportYear]);

  const selectedRecord = useMemo(() => {
    if (!reportDateOptions.length) {
      return null;
    }
    if (!selectedReportDate) {
      return reportDateOptions[0];
    }
    return (
      reportDateOptions.find((row) => {
        const value =
          row['Report_Date_as_YYYY-MM-DD'] || row.As_of_Date_In_Form_YYMMDD || '';
        return value === selectedReportDate;
      }) || reportDateOptions[0]
    );
  }, [reportDateOptions, selectedReportDate]);
  const rowsByMarket = useMemo(() => {
    const map = new Map();
    sampleData.forEach((row) => {
      const marketName = row.Market_and_Exchange_Names;
      const dateISO = getRowReportDateIso(row);
      if (!marketName || !dateISO) {
        return;
      }
      if (!map.has(marketName)) {
        map.set(marketName, []);
      }
      map.get(marketName).push({ row, dateISO });
    });
    map.forEach((entries) => {
      entries.sort((a, b) => b.dateISO.localeCompare(a.dateISO));
    });
    return map;
  }, []);

  const impactReport = useMemo(() => {
    const lookbackWeeks = Number(impactReportRequest.lookbackWeeks) || 4;
    const activeGroups = IMPACT_REPORT_PARTICIPANT_GROUPS.filter((group) =>
      impactReportRequest.groupIds.includes(group.id)
    );
    if (!activeGroups.length) {
      return {
        error: 'Select at least one participant group, then click Generate report.',
      };
    }

    const marketUniverse =
      impactReportRequest.scope === 'current_contract'
        ? selectedMarket
          ? [selectedMarket]
          : []
        : selectedGroupOptions;

    if (!marketUniverse.length) {
      return {
        error: 'No markets are available for the selected scope.',
      };
    }

    const targetDateIso = toIsoReportDate(selectedReportDate);
    const marketRows = [];

    marketUniverse.forEach((marketName) => {
      const rows = rowsByMarket.get(marketName) || [];
      if (!rows.length) {
        return;
      }

      let currentIndex = 0;
      if (targetDateIso) {
        const idx = rows.findIndex((entry) => entry.dateISO <= targetDateIso);
        currentIndex = idx >= 0 ? idx : rows.length - 1;
      }
      const currentEntry = rows[currentIndex];
      if (!currentEntry) {
        return;
      }

      const previousEntry = rows[currentIndex + 1] || null;
      const lookbackEntry = rows[currentIndex + lookbackWeeks] || null;

      let currentNet = 0;
      let weeklyFlowContracts = 0;
      let previousNet = 0;
      let lookbackNet = 0;

      activeGroups.forEach((groupConfig) => {
        const current = computeGroupNetAndFlow(currentEntry.row, groupConfig);
        currentNet += current.net;
        weeklyFlowContracts += current.weeklyDeltaNet;

        if (previousEntry) {
          previousNet += computeGroupNetAndFlow(previousEntry.row, groupConfig).net;
        }
        if (lookbackEntry) {
          lookbackNet += computeGroupNetAndFlow(lookbackEntry.row, groupConfig).net;
        }
      });

      const openInterest = toNumber(currentEntry.row.Open_Interest_All);
      if (!Number.isFinite(openInterest) || openInterest <= 0) {
        return;
      }

      const netPctOi = computeNetPctOI(currentNet, openInterest);
      const weeklyFlowPctOi = computeNetPctOI(weeklyFlowContracts, openInterest);
      const lookbackFlowContracts = lookbackEntry
        ? currentNet - lookbackNet
        : weeklyFlowContracts;
      const lookbackFlowPctOi = computeNetPctOI(lookbackFlowContracts, openInterest);
      const previousNetPctOi = previousEntry
        ? computeNetPctOI(previousNet, openInterest)
        : null;
      const flowScorePctOi = weeklyFlowPctOi * 0.65 + lookbackFlowPctOi * 0.35;
      const bias = describeBias(flowScorePctOi);
      const shortingCaution = classifyShortingCaution({
        flowScorePctOi,
        weeklyFlowPctOi,
        netPctOi,
      });
      const longingCaution = classifyLongingCaution({
        flowScorePctOi,
        weeklyFlowPctOi,
        netPctOi,
      });

      marketRows.push({
        market: marketName,
        reportDateIso: currentEntry.dateISO,
        openInterest,
        currentNetContracts: currentNet,
        netPctOi,
        weeklyFlowContracts,
        weeklyFlowPctOi,
        lookbackFlowContracts,
        lookbackFlowPctOi,
        previousNetPctOi,
        flowScorePctOi,
        bias,
        shortingCaution,
        longingCaution,
      });
    });

    if (!marketRows.length) {
      return {
        error: 'No valid CFTC rows were found for this report scope/date combination.',
      };
    }

    const rankedRows = marketRows
      .slice()
      .sort((a, b) => Math.abs(b.flowScorePctOi) - Math.abs(a.flowScorePctOi));
    const shortingMindful = rankedRows
      .filter((row) => row.shortingCaution !== 'Low' && row.flowScorePctOi > 0)
      .sort((a, b) => b.flowScorePctOi - a.flowScorePctOi)
      .slice(0, 4);
    const bearishSetups = rankedRows
      .filter((row) => row.bias === 'Bearish pressure' && row.shortingCaution === 'Low')
      .sort((a, b) => a.flowScorePctOi - b.flowScorePctOi)
      .slice(0, 4);
    const bullishLeaders = rankedRows
      .filter((row) => row.flowScorePctOi > 0)
      .sort((a, b) => b.flowScorePctOi - a.flowScorePctOi)
      .slice(0, 3);
    const bearishLeaders = rankedRows
      .filter((row) => row.flowScorePctOi < 0)
      .sort((a, b) => a.flowScorePctOi - b.flowScorePctOi)
      .slice(0, 3);

    const groupLabel = activeGroups.map((group) => group.label).join(' + ');
    const scopeLabel =
      impactReportRequest.scope === 'current_contract'
        ? `current contract (${selectedMarket || '—'})`
        : `market group (${selectedMarketGroup || '—'})`;
    const reportDateIso = targetDateIso || rankedRows[0].reportDateIso || '';
    const reportDateLabel = reportDateIso
      ? moment(reportDateIso, 'YYYY-MM-DD', true).format('MMM D, YYYY')
      : 'Date unavailable';
    let dominantFlow = 'mixed';
    if (bullishLeaders.length > bearishLeaders.length) {
      dominantFlow = 'bullish';
    } else if (bearishLeaders.length > bullishLeaders.length) {
      dominantFlow = 'bearish';
    }
    const summary =
      `${groupLabel} flows suggest ${dominantFlow} pressure across ${scopeLabel}. ` +
      'Use this as context, not a standalone trade signal.';

    return {
      error: '',
      summary,
      reportDateLabel,
      groupLabel,
      scopeLabel,
      lookbackWeeks,
      rows: rankedRows,
      shortingMindful,
      bearishSetups,
      bullishLeaders,
      bearishLeaders,
    };
  }, [
    impactReportRequest,
    rowsByMarket,
    selectedGroupOptions,
    selectedMarket,
    selectedMarketGroup,
    selectedReportDate,
  ]);

  const snapshotRows = useMemo(() => {
    if (!selectedRecord) {
      return [];
    }
    const openInterest = toNumber(selectedRecord.Open_Interest_All);
    return SNAPSHOT_GROUPS.map((group) => {
      const long = toNumber(selectedRecord[group.longKey]);
      const short = toNumber(selectedRecord[group.shortKey]);
      const spread = group.spreadKey ? toNumber(selectedRecord[group.spreadKey]) : 0;
      const net = computeNet(long, short);
      return {
        id: group.id,
        label: group.label,
        long,
        short,
        spread,
        net,
        netPct: computeNetPctOI(net, openInterest),
      };
    });
  }, [selectedRecord]);

  const snapshotPctOISeries = useMemo(() => {
    if (!selectedRecord) {
      return null;
    }
    const openInterest = toNumber(selectedRecord.Open_Interest_All);
    if (!Number.isFinite(openInterest) || openInterest <= 0) {
      return null;
    }
    const longSeries = snapshotRows.map((row) => ({
      label: row.label,
      value: (row.long || 0) / openInterest * 100,
    }));
    const shortSeries = snapshotRows.map((row) => ({
      label: row.label,
      value: (row.short || 0) / openInterest * 100,
    }));
    return { longSeries, shortSeries };
  }, [snapshotRows, selectedRecord]);

  const snapshotChangeSeries = useMemo(() => {
    if (!selectedRecord) {
      return null;
    }
    const rows = SNAPSHOT_CHANGE_GROUPS.map((group) => {
      const longChange = toNumber(selectedRecord[group.changeLongKey]);
      const shortChange = toNumber(selectedRecord[group.changeShortKey]);
      if (!Number.isFinite(longChange) && !Number.isFinite(shortChange)) {
        return null;
      }
      const netChange = (Number.isFinite(longChange) ? longChange : 0) - (Number.isFinite(shortChange) ? shortChange : 0);
      return {
        label: group.label,
        longChange,
        shortChange,
        netChange,
      };
    }).filter(Boolean);
    if (!rows.length) {
      return null;
    }
    return rows;
  }, [selectedRecord]);

  const summaryDonutRef = useRef(null);
  useLayoutEffect(() => {
    if (snapshotTab !== 'summary') {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      if (!summaryDonutRef.current) {
        return;
      }
      const plots = summaryDonutRef.current.querySelectorAll('.js-plotly-plot');
      plots.forEach((plot) => {
        if (window.Plotly?.Plots?.resize) {
          window.Plotly.Plots.resize(plot);
        }
      });
    }, 50);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [snapshotTab, selectedReportDate]);

  const openInterestDistribution = useMemo(() => {
    if (!snapshotRows.length) {
      return null;
    }
    const slices = snapshotRows
      .map((row) => {
        const long = row.long || 0;
        const short = row.short || 0;
        const spread = row.spread || 0;
        return {
          label: row.label,
          value: long + short + spread,
          long,
          short,
          spread,
        };
      })
      .filter((slice) => Number.isFinite(slice.value) && slice.value > 0);
    if (!slices.length) {
      return null;
    }
    const total = slices.reduce((sum, slice) => sum + slice.value, 0);
    return { slices, total };
  }, [snapshotRows]);

  const openInterestSideDistribution = useMemo(() => {
    if (!snapshotRows.length) {
      return null;
    }
    const reportableLong = toNumber(selectedRecord?.Tot_Rept_Positions_Long_All);
    const reportableShort = toNumber(selectedRecord?.Tot_Rept_Positions_Short_All);
    const nonRepLong = toNumber(selectedRecord?.NonRept_Positions_Long_All);
    const nonRepShort = toNumber(selectedRecord?.NonRept_Positions_Short_All);
    const hasTotals =
      Number.isFinite(reportableLong) &&
      Number.isFinite(reportableShort) &&
      (reportableLong + nonRepLong > 0 || reportableShort + nonRepShort > 0);
    const longTotal = hasTotals
      ? reportableLong + nonRepLong
      : snapshotRows.reduce((sum, row) => sum + (row.long || 0), 0);
    const shortTotal = hasTotals
      ? reportableShort + nonRepShort
      : snapshotRows.reduce((sum, row) => sum + (row.short || 0), 0);
    if (!Number.isFinite(longTotal) || !Number.isFinite(shortTotal)) {
      return null;
    }
    if (longTotal <= 0 && shortTotal <= 0) {
      return null;
    }
    return [
      { label: 'Long', value: longTotal },
      { label: 'Short', value: shortTotal },
    ];
  }, [snapshotRows, selectedRecord]);

  const snapshotOpenInterestTotal = useMemo(() => {
    if (!selectedRecord) {
      return null;
    }
    const value = toNumber(selectedRecord.Open_Interest_All);
    return Number.isFinite(value) ? value : null;
  }, [selectedRecord]);

  const plotlyThemeKey =
    typeof window !== 'undefined'
      ? document.documentElement.getAttribute('data-theme') || 'dark'
      : 'dark';
  const plotlyPanelBg = useMemo(() => {
    if (typeof window === 'undefined') {
      return 'var(--color-bg-panel)';
    }
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue('--color-bg-panel')
      .trim();
    return value || 'var(--color-bg-panel)';
  }, [plotlyThemeKey]);
  const plotlyTextColor = useMemo(() => {
    if (typeof window === 'undefined') {
      return 'var(--color-text-strong)';
    }
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue('--color-text-strong')
      .trim();
    return value || 'var(--color-text-strong)';
  }, [plotlyThemeKey]);
  const plotlyWarningColor = useMemo(() => {
    if (typeof window === 'undefined') {
      return 'var(--color-warning)';
    }
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue('--color-warning')
      .trim();
    return value || 'var(--color-warning)';
  }, [plotlyThemeKey]);
  const plotlyAccentColor = useMemo(() => {
    if (typeof window === 'undefined') {
      return 'var(--color-accent)';
    }
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue('--color-accent')
      .trim();
    return value || 'var(--color-accent)';
  }, [plotlyThemeKey]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlView = params.get('view');
    const storedView = window.localStorage.getItem('cftcPositionView');
    const candidate = urlView || storedView || 'net';
    if (POSITION_VIEW_OPTIONS.some((option) => option.value === candidate)) {
      setPositionView(candidate);
    } else {
      setPositionView('net');
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem('cftcPositionView', positionView);
    const params = new URLSearchParams(window.location.search);
    params.set('view', positionView);
    const nextUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', nextUrl);
  }, [positionView]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(
        'ui.grossActivityBadge.enabled',
        grossActivityBadgeEnabled ? 'true' : 'false'
      );
    } catch (err) {
      // Ignore storage failures.
    }
  }, [grossActivityBadgeEnabled]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem('cftcTvSourceByMarket', JSON.stringify(tvSourceByMarket));
    } catch (err) {
      // Ignore storage failures.
    }
  }, [tvSourceByMarket]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem('cftcTvIntervalByMarket', JSON.stringify(tvIntervalByMarket));
    } catch (err) {
      // Ignore storage failures.
    }
  }, [tvIntervalByMarket]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem('cftcTvRangeByMarket', JSON.stringify(tvRangeByMarket));
    } catch (err) {
      // Ignore storage failures.
    }
  }, [tvRangeByMarket]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(
        'cftcTvSymbolOverrides',
        JSON.stringify(tvSymbolOverrideByMarket)
      );
    } catch (err) {
      // Ignore storage failures.
    }
  }, [tvSymbolOverrideByMarket]);

  useEffect(() => {
    if (extremesTouched) {
      return;
    }
    if (positionView === 'long' || positionView === 'short') {
      setExtremesMode('off');
      return;
    }
    setExtremesMode('1y');
  }, [positionView, extremesTouched]);

  useEffect(() => {
    if (!selectedMarket) {
      return;
    }
    if (lastCorrelationMarketRef.current === selectedMarket) {
      return;
    }
    lastCorrelationMarketRef.current = selectedMarket;
    if (correlationTouchedRef.current) {
      return;
    }
    // Presets reflect typical flow/response cadence by asset class.
    const assetClass = getAssetClassForMarket(selectedMarket, currencyPair);
    const preset = CORRELATION_PRESETS[assetClass] || CORRELATION_PRESETS.equity;
    setCorrelationMetric(preset.metric);
    setCorrelationHorizon(preset.horizon);
    setCorrelationLag(preset.lag);
    setCorrelationMethod(preset.method);
  }, [selectedMarket, currencyPair]);

  useEffect(() => {
    if (!tvSymbol) {
      return undefined;
    }
    let canceled = false;
    loadTradingViewScript()
      .then(() => {
        if (canceled) {
          return;
        }
        const container = document.getElementById(tvContainerId.current);
        if (!container || !window.TradingView) {
          return;
        }
        container.innerHTML = '';
        const theme =
          document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
        // eslint-disable-next-line no-new
        new window.TradingView.widget({
          symbol: tvSymbol,
          interval: tvInterval,
          range: tvRange,
          container_id: tvContainerId.current,
          theme,
          autosize: true,
          locale: 'en',
          style: '1',
          hide_side_toolbar: true,
          allow_symbol_change: false,
        });
      })
      .catch(() => {});
    return () => {
      canceled = true;
    };
  }, [tvSymbol, tvInterval, tvRange]);

  useEffect(() => {
    if (!selectedMarket) {
      return;
    }
    setTickerEditMap((prev) => ({ ...prev, [selectedMarket]: false }));
  }, [selectedMarket]);

  useEffect(() => {
    if (!isTrendView) {
      setShowPositioningTour(false);
      return;
    }
    if (hasSeenPositioningTour || showPositioningTour) {
      return;
    }
    if (!correlationTitleRef.current) {
      return;
    }
    setShowPositioningTour(true);
    setPositioningTourStep(0);
  }, [isTrendView, hasSeenPositioningTour, showPositioningTour]);

  useEffect(() => {
    if (!showPositioningTour) {
      setPositioningTourPosition(null);
      return;
    }
    const updatePosition = () => {
      const anchors = {
        title: correlationTitleRef,
        metric: correlationMetricRef,
        horizon: correlationHorizonRef,
        lag: correlationLagRef,
        rho: correlationRhoRef,
        confidence: correlationConfidenceRef,
        curve: correlationSparklineRef,
        footer: correlationFooterRef,
      };
      const currentStep = positioningTourSteps[positioningTourStep];
      const anchorRef = currentStep ? anchors[currentStep.anchor] : null;
      const anchor = anchorRef?.current;
      if (!anchor) {
        setPositioningTourPosition(null);
        return;
      }
      const rect = anchor.getBoundingClientRect();
      const tooltipWidth = 320;
      const tooltipHeight = 180;
      let top = rect.bottom + 8;
      if (top + tooltipHeight > window.innerHeight) {
        top = rect.top - tooltipHeight - 8;
      }
      top = Math.max(8, top);
      let left = rect.left;
      if (left + tooltipWidth > window.innerWidth - 8) {
        left = window.innerWidth - tooltipWidth - 8;
      }
      left = Math.max(8, left);
      setPositioningTourPosition({ top, left });
    };
    const raf = window.requestAnimationFrame(updatePosition);
    const handleResize = () => updatePosition();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [showPositioningTour, positioningTourStep, positioningTourSteps]);

  useEffect(() => {
    if (isTrendView) {
      return;
    }
    setSignalsOnboarding((prev) => {
      if (!prev.isOpen && !prev.dismissed) {
        return prev;
      }
      return { ...prev, isOpen: false, dismissed: false };
    });
  }, [isTrendView]);

  useEffect(() => {
    if (!isTrendView) {
      return;
    }
    setCftcSummaryOnboarding((prev) => {
      if (!prev.isOpen && !prev.dismissed) {
        return prev;
      }
      return { ...prev, isOpen: false, dismissed: false };
    });
  }, [isTrendView]);

  useEffect(() => {
    if (
      !shouldAutoOpenSignalsOnboarding(signalsOnboarding, {
        isTrendView,
        positioningTourOpen: showPositioningTour,
      })
    ) {
      return;
    }
    setSignalsOnboarding((prev) => ({ ...prev, isOpen: true, step: 0 }));
  }, [isTrendView, showPositioningTour, signalsOnboarding]);

  useEffect(() => {
    if (
      !shouldAutoOpenCftcSummaryOnboarding(cftcSummaryOnboarding, {
        isTrendView,
        positioningTourOpen: showPositioningTour,
        otherTourOpen: signalsOnboarding.isOpen || positionTrendsOnboarding.isOpen,
      })
    ) {
      return;
    }
    setCftcSummaryOnboarding((prev) => ({ ...prev, isOpen: true, step: 0 }));
  }, [
    cftcSummaryOnboarding,
    isTrendView,
    showPositioningTour,
    signalsOnboarding.isOpen,
    positionTrendsOnboarding.isOpen,
  ]);

  useEffect(() => {
    if (!filteredByMarket.length || selectedReportDate) {
      return;
    }
      const firstDate =
        filteredByMarket[0]['Report_Date_as_YYYY-MM-DD'] || filteredByMarket[0].As_of_Date_In_Form_YYMMDD;
    if (firstDate) {
      setSelectedReportDate(firstDate);
    }
  }, [filteredByMarket, selectedReportDate]);

  useEffect(() => {
    if (!reportDateOptions.length) {
      return;
    }
    const mostRecent =
      reportDateOptions[0]['Report_Date_as_YYYY-MM-DD'] ||
      reportDateOptions[0].As_of_Date_In_Form_YYMMDD;
    if (!mostRecent) {
      return;
    }
    const date = moment(mostRecent, ['YYYY-MM-DD', 'YYMMDD'], true);
    if (!date.isValid()) {
      return;
    }
    const year = date.format('YYYY');
    const month = date.format('MM');
    if (!selectedReportYear) {
      setSelectedReportYear(year);
    }
    if (!selectedReportMonth) {
      setSelectedReportMonth(month);
    }
    if (!selectedReportDate) {
      setSelectedReportDate(mostRecent);
    }
  }, [reportDateOptions, selectedReportDate, selectedReportMonth, selectedReportYear]);

  useEffect(() => {
    if (!reportDateOptions.length) {
      return;
    }
    if (lastMarketRef.current === selectedMarket) {
      return;
    }
    lastMarketRef.current = selectedMarket;
    const mostRecent =
      reportDateOptions[0]['Report_Date_as_YYYY-MM-DD'] ||
      reportDateOptions[0].As_of_Date_In_Form_YYMMDD;
    if (!mostRecent) {
      return;
    }
    const date = moment(mostRecent, ['YYYY-MM-DD', 'YYMMDD'], true);
    if (!date.isValid()) {
      return;
    }
    setSelectedReportYear(date.format('YYYY'));
    setSelectedReportMonth(date.format('MM'));
    setSelectedReportDate(mostRecent);
  }, [reportDateOptions, selectedMarket]);

  useEffect(() => {
    if (!reportDateOptions.length || !trendMode) {
      return;
    }
    if (lastTrendMarketRef.current === selectedMarket) {
      return;
    }
    lastTrendMarketRef.current = selectedMarket;
    const mostRecent =
      reportDateOptions[0]['Report_Date_as_YYYY-MM-DD'] ||
      reportDateOptions[0].As_of_Date_In_Form_YYMMDD;
    const mostRecentDate = moment(mostRecent, ['YYYY-MM-DD', 'YYMMDD'], true);
    if (!mostRecentDate.isValid()) {
      return;
    }
    const endYear = mostRecentDate.format('YYYY');
    const endMonth = mostRecentDate.format('MM');
    const startDate = mostRecentDate.clone().subtract(6, 'months');
    setTrendEndYear(endYear);
    setTrendEndMonth(endMonth);
    setTrendStartYear(startDate.format('YYYY'));
    setTrendStartMonth(startDate.format('MM'));
  }, [reportDateOptions, selectedMarket, trendMode]);

  useEffect(() => {
    if (!trendStartYear || trendStartMonthOptions.length === 0) {
      return;
    }
    if (trendStartMonth && trendStartMonthOptions.includes(trendStartMonth)) {
      return;
    }
    setTrendStartMonth(trendStartMonthOptions[0]);
  }, [trendStartMonth, trendStartMonthOptions, trendStartYear]);

  useEffect(() => {
    if (!trendEndYear || trendEndMonthOptions.length === 0) {
      return;
    }
    if (trendEndMonth && trendEndMonthOptions.includes(trendEndMonth)) {
      return;
    }
    setTrendEndMonth(trendEndMonthOptions[trendEndMonthOptions.length - 1]);
  }, [trendEndMonth, trendEndMonthOptions, trendEndYear]);

  const trendRangeDates = useMemo(() => {
    if (!trendStartYear || !trendStartMonth || !trendEndYear || !trendEndMonth) {
      return null;
    }
    const start = moment(`${trendStartYear}-${trendStartMonth}-01`, 'YYYY-MM-DD', true);
    const end = moment(`${trendEndYear}-${trendEndMonth}-01`, 'YYYY-MM-DD', true).endOf('month');
    if (!start.isValid() || !end.isValid()) {
      return null;
    }
    if (end.isBefore(start)) {
      return { start: end.clone().startOf('month'), end: start.clone().endOf('month') };
    }
    return { start, end };
  }, [trendStartMonth, trendStartYear, trendEndMonth, trendEndYear]);
  const trendRangeKey = useMemo(() => {
    if (trendRangeDates) {
      return `${trendRangeDates.start.toISOString()}-${trendRangeDates.end.toISOString()}`;
    }
    if (!reportDateOptions.length) {
      return '';
    }
    const newest =
      reportDateOptions[0]['Report_Date_as_YYYY-MM-DD'] ||
      reportDateOptions[0].As_of_Date_In_Form_YYMMDD;
    const oldest =
      reportDateOptions[reportDateOptions.length - 1]['Report_Date_as_YYYY-MM-DD'] ||
      reportDateOptions[reportDateOptions.length - 1].As_of_Date_In_Form_YYMMDD;
    const newestDate = moment(newest, ['YYYY-MM-DD', 'YYMMDD'], true);
    const oldestDate = moment(oldest, ['YYYY-MM-DD', 'YYMMDD'], true);
    if (!newestDate.isValid() || !oldestDate.isValid()) {
      return '';
    }
    const start = oldestDate.clone().startOf('month');
    const end = newestDate.clone().endOf('month');
    return `${start.toISOString()}-${end.toISOString()}`;
  }, [reportDateOptions, trendRangeDates]);
  const trendPriceInterval = priceReferenceMode === 'live' ? 'daily' : 'weekly';

  useEffect(() => {
    if (!currencyPair || !trendRangeKey) {
      setTrendPriceData([]);
      setTrendPriceMeta({ pair: '', rangeKey: '', interval: '' });
      return;
    }
    const requestId = ++trendPriceRequestRef.current;
    const payload = {
      currencyPair,
      timeInterval: trendPriceInterval,
    };
    fetch(process.env.REACT_APP_FX_INTRADAY_API_BASE || 'https://vidplay-api.deng-software.com/fx', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-store',
      },
      cache: 'no-store',
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`FX request failed: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (requestId !== trendPriceRequestRef.current) {
          return;
        }
        const rows = Array.isArray(data?.data) ? data.data : [];
        setTrendPriceData(rows);
        setTrendPriceMeta({ pair: currencyPair, rangeKey: trendRangeKey, interval: trendPriceInterval });
      })
      .catch(() => {
        if (requestId !== trendPriceRequestRef.current) {
          return;
        }
        setTrendPriceData([]);
        setTrendPriceMeta({ pair: currencyPair, rangeKey: trendRangeKey, interval: trendPriceInterval });
      });
  }, [currencyPair, trendRangeKey, trendPriceInterval]);

  const sortedTrendPriceData = useMemo(() => {
    if (!trendPriceData.length) {
      return [];
    }
    return trendPriceData
      .filter((point) => point && point.time)
      .slice()
      .sort((a, b) => new Date(a.time) - new Date(b.time));
  }, [trendPriceData]);

  const latestTrendPricePoint = sortedTrendPriceData.length
    ? sortedTrendPriceData[sortedTrendPriceData.length - 1]
    : null;
  const latestTrendPriceDate = latestTrendPricePoint
    ? moment(latestTrendPricePoint.time).format("YYYY-MM-DD")
    : null;

  const filteredByMarketForTrend = useMemo(() => {
    if (!trendMode || !trendRangeDates) {
      return filteredByMarket;
    }
    return filteredByMarket.filter((row) => {
      const value =
        row['Report_Date_as_YYYY-MM-DD'] || row.As_of_Date_In_Form_YYMMDD || '';
      const date = moment(value, ['YYYY-MM-DD', 'YYMMDD'], true);
      return date.isValid() && date.isBetween(trendRangeDates.start, trendRangeDates.end, null, '[]');
    });
  }, [filteredByMarket, trendMode, trendRangeDates]);
  const trendPriceLookup = useMemo(() => {
    if (
      !trendPriceData.length ||
      !filteredByMarketForTrend.length ||
      trendPriceMeta.pair !== currencyPair ||
      trendPriceMeta.rangeKey !== trendRangeKey ||
      trendPriceMeta.interval !== trendPriceInterval
    ) {
      return new Map();
    }
    const map = new Map();
    const sortedPriceData = trendPriceData
      .map((point) => ({
        dateOnly: moment(point.time).format('YYYY-MM-DD'),
        close: point.close,
      }))
      .sort((a, b) => new Date(a.dateOnly) - new Date(b.dateOnly));
    const sortedRows = filteredByMarketForTrend
      .slice()
      .sort((a, b) => {
        const dateA = new Date(a['Report_Date_as_YYYY-MM-DD']);
        const dateB = new Date(b['Report_Date_as_YYYY-MM-DD']);
        return dateA - dateB;
      });
    let index = 0;
    let lastClose = null;
    sortedRows.forEach((row) => {
      const reportDate =
        row['Report_Date_as_YYYY-MM-DD'] || row.As_of_Date_In_Form_YYMMDD || '';
      const reportMoment = moment(reportDate, ['YYYY-MM-DD', 'YYMMDD'], true);
      if (!reportMoment.isValid()) {
        return;
      }
      while (
        index < sortedPriceData.length &&
        moment(sortedPriceData[index].dateOnly, 'YYYY-MM-DD').isSameOrBefore(reportMoment)
      ) {
        lastClose = sortedPriceData[index].close;
        index += 1;
      }
      if (lastClose == null && index < sortedPriceData.length) {
        lastClose = sortedPriceData[index].close;
      }
      map.set(reportDate, lastClose);
    });
    return map;
  }, [
    filteredByMarketForTrend,
    trendPriceData,
    trendPriceMeta,
    currencyPair,
    trendRangeKey,
    trendPriceInterval,
  ]);

  const getDailyCloseOnOrBefore = (targetDate) => {
    if (!targetDate || !sortedTrendPriceData.length) {
      return null;
    }
    const targetMoment = moment(targetDate);
    if (!targetMoment.isValid()) {
      return null;
    }
    let lastClose = null;
    for (const point of sortedTrendPriceData) {
      const pointMoment = moment(point.time);
      if (!pointMoment.isValid()) {
        continue;
      }
      if (pointMoment.isAfter(targetMoment)) {
        break;
      }
      if (point.close != null) {
        lastClose = Number(point.close);
      }
    }
    return lastClose;
  };

  const computeDailyPriceReturn = (asOfDate, weeks) => {
    if (!asOfDate) {
      return null;
    }
    const latestClose = getDailyCloseOnOrBefore(asOfDate);
    if (latestClose == null) {
      return null;
    }
    const priorDate = moment(asOfDate).subtract(weeks * 7, 'days').format('YYYY-MM-DD');
    const priorClose = getDailyCloseOnOrBefore(priorDate);
    if (priorClose == null) {
      return null;
    }
    return latestClose - priorClose;
  };

  const trendBaseSeries = useMemo(() => {
    if (!filteredByMarketForTrend.length) {
      return [];
    }
    return filteredByMarketForTrend
      .slice()
      .sort((a, b) => {
        const dateA = new Date(a['Report_Date_as_YYYY-MM-DD']);
        const dateB = new Date(b['Report_Date_as_YYYY-MM-DD']);
        return dateA - dateB;
      })
      .map((row) => {
        const date =
          row['Report_Date_as_YYYY-MM-DD'] || row.As_of_Date_In_Form_YYMMDD || '';
        const formattedDate = date
          ? moment(date).format('MM/DD')
          : `#${filteredByMarketForTrend.indexOf(row) + 1}`;
        const openInterest = toNumber(row.Open_Interest_All);
        return {
          date: formattedDate,
          dateFull: date,
          openInterest,
          DealerLong: toNumber(row.Dealer_Positions_Long_All),
          DealerShort: toNumber(row.Dealer_Positions_Short_All),
          DealerSpread: toNumber(row.Dealer_Positions_Spread_All),
          AssetManagerLong: toNumber(row.Asset_Mgr_Positions_Long_All),
          AssetManagerShort: toNumber(row.Asset_Mgr_Positions_Short_All),
          AssetManagerSpread: toNumber(row.Asset_Mgr_Positions_Spread_All),
          LeveragedMoneyLong: toNumber(row.Lev_Money_Positions_Long_All),
          LeveragedMoneyShort: toNumber(row.Lev_Money_Positions_Short_All),
          LeveragedMoneySpread: toNumber(row.Lev_Money_Positions_Spread_All),
          price: null,
        };
      });
  }, [filteredByMarketForTrend]);

  const trendValueSets = useMemo(() => {
    if (!trendBaseSeries.length) {
      return null;
    }
    const dealerNet = trendBaseSeries.map((row) => computeNet(row.DealerLong, row.DealerShort));
    const assetNet = trendBaseSeries.map((row) =>
      computeNet(row.AssetManagerLong, row.AssetManagerShort)
    );
    const levNet = trendBaseSeries.map((row) =>
      computeNet(row.LeveragedMoneyLong, row.LeveragedMoneyShort)
    );

    let dealerValues = dealerNet;
    let assetValues = assetNet;
    let levValues = levNet;

    if (positionView === 'long') {
      dealerValues = trendBaseSeries.map((row) => row.DealerLong);
      assetValues = trendBaseSeries.map((row) => row.AssetManagerLong);
      levValues = trendBaseSeries.map((row) => row.LeveragedMoneyLong);
    } else if (positionView === 'short') {
      dealerValues = trendBaseSeries.map((row) => row.DealerShort);
      assetValues = trendBaseSeries.map((row) => row.AssetManagerShort);
      levValues = trendBaseSeries.map((row) => row.LeveragedMoneyShort);
    } else if (positionView === 'net_pct_oi') {
      dealerValues = trendBaseSeries.map((row, idx) =>
        computeNetPctOI(dealerNet[idx], row.openInterest)
      );
      assetValues = trendBaseSeries.map((row, idx) =>
        computeNetPctOI(assetNet[idx], row.openInterest)
      );
      levValues = trendBaseSeries.map((row, idx) =>
        computeNetPctOI(levNet[idx], row.openInterest)
      );
    } else if (positionView === 'zscore') {
      dealerValues = computeZScores(dealerNet);
      assetValues = computeZScores(assetNet);
      levValues = computeZScores(levNet);
    }

    return {
      dealerNet,
      assetNet,
      levNet,
      dealerValues,
      assetValues,
      levValues,
    };
  }, [trendBaseSeries, positionView]);

  const showExtremes1y = extremesMode === '1y' || extremesMode === 'both';
  const showExtremes3y = extremesMode === '3y' || extremesMode === 'both';

  const trendBands = useMemo(() => {
    if (!trendValueSets) {
      return null;
    }
    const bands = {};
    if (showExtremes1y) {
      // 1Y window with P10-P90 bands.
      bands.oneYear = {
        dealer: rollingPercentileBands(trendValueSets.dealerValues, 52, [0.1, 0.9]),
        asset: rollingPercentileBands(trendValueSets.assetValues, 52, [0.1, 0.9]),
        lev: rollingPercentileBands(trendValueSets.levValues, 52, [0.1, 0.9]),
      };
    }
    if (showExtremes3y) {
      // 3Y window with P5-P95 bands.
      bands.threeYear = {
        dealer: rollingPercentileBands(trendValueSets.dealerValues, 156, [0.05, 0.95]),
        asset: rollingPercentileBands(trendValueSets.assetValues, 156, [0.05, 0.95]),
        lev: rollingPercentileBands(trendValueSets.levValues, 156, [0.05, 0.95]),
      };
    }
    return bands;
  }, [trendValueSets, showExtremes1y, showExtremes3y]);

  const grossActivityThresholds = useMemo(() => {
    const fallback = {
      FX: { low: 25, high: 75 },
      Rates: { low: 30, high: 70 },
      Equities: { low: 20, high: 80 },
    };
    const rulesThresholds = rulesAssetConfig?.thresholds || {};
    const assetFallback = fallback[rulesAssetClass] || fallback.FX;
    const low =
      typeof rulesThresholds.grossActivityLow === 'number'
        ? rulesThresholds.grossActivityLow
        : assetFallback.low;
    const high =
      typeof rulesThresholds.grossActivityHigh === 'number'
        ? rulesThresholds.grossActivityHigh
        : assetFallback.high;
    return { low, high };
  }, [rulesAssetClass, rulesAssetConfig]);

  const grossActivity = useMemo(() => {
    if (!trendBaseSeries.length) {
      return null;
    }
    const lookbackWindow = extremesMode === '3y' || extremesMode === 'both' ? 156 : 52;
    const combinedSeries = trendBaseSeries.map((row) => computeGrossCombinedPctOI(row));
    const latestCombined = combinedSeries[combinedSeries.length - 1];
    const windowSeries = combinedSeries
      .slice(Math.max(0, combinedSeries.length - lookbackWindow))
      .filter((value) => typeof value === 'number' && Number.isFinite(value));
    const insufficientHistory = windowSeries.length < 20;
    if (!Number.isFinite(latestCombined)) {
      return {
        label: 'Normal',
        percentile: null,
        insufficientHistory: true,
        missingOpenInterest: true,
      };
    }
    const percentile = computePercentile(windowSeries, latestCombined);
    const label = insufficientHistory
      ? 'Normal'
      : classifyGrossActivity(percentile, grossActivityThresholds);
    return {
      label,
      percentile,
      insufficientHistory,
      missingOpenInterest: false,
    };
  }, [trendBaseSeries, extremesMode, grossActivityThresholds]);

  useEffect(() => {
    if (!grossActivity?.missingOpenInterest) {
      return;
    }
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[GrossActivity] Missing or zero open interest for gross activity.', {
        market: selectedMarket,
      });
    }
  }, [grossActivity, selectedMarket]);

  const trendSeries = useMemo(() => {
    if (!trendBaseSeries.length || !trendValueSets) {
      return [];
    }
    const buildBandFields = (band, prefix) => {
      if (!band || band.lower == null || band.upper == null) {
        return {
          [`${prefix}Lower`]: null,
          [`${prefix}Range`]: null,
        };
      }
      return {
        [`${prefix}Lower`]: band.lower,
        [`${prefix}Range`]: band.upper - band.lower,
      };
    };

    return trendBaseSeries.map((row, index) => {
      const oneYear = trendBands?.oneYear;
      const threeYear = trendBands?.threeYear;
      return {
        date: row.date,
        dateFull: row.dateFull,
        price: trendPriceLookup.get(row.dateFull) ?? row.price,
        openInterest: row.openInterest,
        Dealer: trendValueSets.dealerValues[index],
        AssetManager: trendValueSets.assetValues[index],
        LeveragedMoney: trendValueSets.levValues[index],
        dealerLong: row.DealerLong,
        dealerShort: row.DealerShort,
        assetLong: row.AssetManagerLong,
        assetShort: row.AssetManagerShort,
        levLong: row.LeveragedMoneyLong,
        levShort: row.LeveragedMoneyShort,
        dealerNet: trendValueSets.dealerNet[index],
        assetNet: trendValueSets.assetNet[index],
        levNet: trendValueSets.levNet[index],
        dealerNetPct: computeNetPctOI(trendValueSets.dealerNet[index], row.openInterest),
        assetNetPct: computeNetPctOI(trendValueSets.assetNet[index], row.openInterest),
        levNetPct: computeNetPctOI(trendValueSets.levNet[index], row.openInterest),
        ...buildBandFields(oneYear?.dealer?.[index], 'dealerBand1y'),
        ...buildBandFields(oneYear?.asset?.[index], 'assetBand1y'),
        ...buildBandFields(oneYear?.lev?.[index], 'levBand1y'),
        ...buildBandFields(threeYear?.dealer?.[index], 'dealerBand3y'),
        ...buildBandFields(threeYear?.asset?.[index], 'assetBand3y'),
        ...buildBandFields(threeYear?.lev?.[index], 'levBand3y'),
      };
    });
  }, [trendBaseSeries, trendValueSets, trendBands, trendPriceLookup]);

  const trendInsight = useMemo(() => {
    if (!trendSeries.length) {
      return null;
    }
    const latestIndex = trendSeries.length - 1;
    const latest = trendSeries[latestIndex];
    const prev = trendSeries[latestIndex - 1];
    if (!latest || !prev) {
      return null;
    }
    const assetNetSeries = trendSeries.map((row) => row.assetNet);
    const levNetSeries = trendSeries.map((row) => row.levNet);
    const assetPercentiles = computePercentiles(assetNetSeries);
    const levPercentiles = computePercentiles(levNetSeries);
    const levZScores = computeZScores(levNetSeries);
    const latestAssetPercentile = assetPercentiles[latestIndex] ?? 0;
    const latestLevPercentile = levPercentiles[latestIndex] ?? 0;
    const latestLevZScore = levZScores[latestIndex] ?? 0;

    const lookbackDays = 20; // breakout context uses last ~1 month of daily closes.
    const priceSeries = trendPriceData.map((point) => point.close);
    const priceBreakout = (() => {
      if (priceSeries.length <= lookbackDays) {
        return false;
      }
      const recent = priceSeries[priceSeries.length - 1];
      const prior = priceSeries.slice(priceSeries.length - 1 - lookbackDays, -1);
      const maxPrior = Math.max(...prior);
      const minPrior = Math.min(...prior);
      return recent > maxPrior || recent < minPrior;
    })();

    const combinedNetSeries = trendSeries.map((row) =>
      computeNet(row.assetLong + row.levLong, row.assetShort + row.levShort)
    );
    const combinedNetPctSeries = trendSeries.map((row, idx) =>
      computeNetPctOI(combinedNetSeries[idx], row.openInterest)
    );
    const netPctLookback = Math.max(0, latestIndex - 2); // ~2-3 weeks back in weekly data.
    const netPctOIChange =
      combinedNetPctSeries[latestIndex] - combinedNetPctSeries[netPctLookback];

    const dealerNetShort = Math.max(0, -latest.dealerNet);
    const prevDealerNetShort = Math.max(0, -prev.dealerNet);
    const dealerNetShortChange = dealerNetShort - prevDealerNetShort;

    return getPositioningInsight({
      leveragedZScore: latestLevZScore,
      assetNet: latest.assetNet,
      assetPercentile: latestAssetPercentile,
      leveragedPercentile: latestLevPercentile,
      priceBreakout,
      netPctOIChange,
      assetNetChange: latest.assetNet - prev.assetNet,
      openInterestChange: latest.openInterest - prev.openInterest,
      dealerNetShortChange,
    });
  }, [trendSeries, trendPriceData]);

  const correlationData = useMemo(() => {
    if (!trendSeries.length) {
      return {
        deltaSeries: [],
        returnSeries: [],
      };
    }
    // Use combined net across tracked groups for a single positioning series.
    const combinedNet = trendSeries.map((row) => row.dealerNet + row.assetNet + row.levNet);
    const combinedNetPct = trendSeries.map((row, index) =>
      computeNetPctOI(combinedNet[index], row.openInterest)
    );
    const baseSeries =
      correlationMetric === 'delta_net_pct_oi' ? combinedNetPct : combinedNet;
    return {
      deltaSeries: computeDelta(baseSeries),
      returnSeries: computeReturns(trendSeries.map((row) => row.price), correlationHorizon),
    };
  }, [trendSeries, correlationMetric, correlationHorizon]);

  const correlationStats = useMemo(() => {
    if (!trendSeries.length) {
      return { correlation: null, sampleSize: 0 };
    }
    // Lead/Lag shifts the start of the return window relative to positioning changes.
    return computeLaggedCorrelation(
      correlationData.deltaSeries,
      correlationData.returnSeries,
      correlationLag,
      correlationMethod
    );
  }, [trendSeries.length, correlationData, correlationLag, correlationMethod]);

  const correlationSummary = useMemo(() => {
    const sampleSize = correlationStats.sampleSize || 0;
    const correlationValue =
      correlationStats.correlation != null && Number.isFinite(correlationStats.correlation)
        ? correlationStats.correlation
        : null;
    const isSufficient = sampleSize >= 20 && correlationValue != null;
    const lagLabel =
      correlationLag > 0
        ? 'Positioning leads price'
        : correlationLag < 0
        ? 'Price leads positioning'
        : 'Same-date correlation';
    const strengthBucket = isSufficient ? getCorrelationStrengthBucket(correlationValue) : null;
    const interpretationText = getCorrelationInterpretation(correlationLag);
    const lagWeeks = Math.abs(correlationLag);
    const lagExplanation =
      correlationLag === 0
        ? `Lead/Lag 0: returns start the same week as positioning (H=${correlationHorizon}w).`
        : `Lead/Lag ${correlationLag > 0 ? '+' : ''}${correlationLag}: returns start ${lagWeeks} week${
            lagWeeks === 1 ? '' : 's'
          } ${correlationLag > 0 ? 'after' : 'before'} the CFTC date (H=${correlationHorizon}w).`;
    return {
      sampleSize,
      correlationValue,
      isSufficient,
      lagLabel,
      strengthBucket,
      interpretationText,
      lagExplanation,
    };
  }, [correlationStats, correlationLag, correlationHorizon]);

  const correlationSparklineData = useMemo(() => {
    if (!trendSeries.length) {
      return [];
    }
    const data = [];
    let maxAbs = null;
    let maxPoint = null;
    for (let lag = -8; lag <= 8; lag += 1) {
      const { correlation, sampleSize } = computeLaggedCorrelation(
        correlationData.deltaSeries,
        correlationData.returnSeries,
        lag,
        correlationMethod
      );
      // Hide rho values when sample size is too small to be meaningful.
      const rho =
        sampleSize >= 20 && correlation != null && Number.isFinite(correlation)
          ? correlation
          : null;
      const point = { lag, rho, sampleSize };
      if (rho != null) {
        const absValue = Math.abs(rho);
        if (maxAbs == null || absValue > maxAbs) {
          maxAbs = absValue;
          maxPoint = point;
        }
      }
      data.push(point);
    }
    if (maxPoint) {
      return data.map((point) => ({
        ...point,
        isMaxAbs: point.lag === maxPoint.lag,
      }));
    }
    return data;
  }, [trendSeries.length, correlationData, correlationMethod]);

  const selectedSparklinePoint = useMemo(() => {
    return correlationSparklineData.find((point) => point.lag === correlationLag) || null;
  }, [correlationSparklineData, correlationLag]);

  const maxAbsSparklinePoint = useMemo(() => {
    return correlationSparklineData.find((point) => point.isMaxAbs) || null;
  }, [correlationSparklineData]);
  const correlationConfidence = useMemo(() => {
    return getConfidenceBadge(
      correlationSummary.correlationValue,
      correlationSummary.sampleSize,
      correlationSparklineData,
      correlationLag
    );
  }, [
    correlationSummary.correlationValue,
    correlationSummary.sampleSize,
    correlationSparklineData,
    correlationLag,
  ]);

  const signalsBase = useMemo(() => {
    if (!trendSeries.length || !rulesAssetConfig) {
      return null;
    }
    const latestIndex = trendSeries.length - 1;
    const latest = trendSeries[latestIndex];
    if (!latest) {
      return null;
    }
    const levNetSeries = trendSeries.map((row) => row.levNet);
    const amNetSeries = trendSeries.map((row) => row.assetNet);
    const dealerNetSeries = trendSeries.map((row) => row.dealerNet);
    const levNetPercentiles = computePercentiles(levNetSeries);
    const amNetPercentiles = computePercentiles(amNetSeries);
    const dealerNetPercentiles = computePercentiles(dealerNetSeries);
    const levNetZScores = computeZScores(levNetSeries);
    const previousWeek = trendSeries[latestIndex - 1];
    const previousFourWeeks = trendSeries[latestIndex - 4];
    const oiChange4w =
      previousFourWeeks?.openInterest != null
        ? latest.openInterest - previousFourWeeks.openInterest
        : null;
    const asOfMoment = moment(latest.dateFull, ['YYYY-MM-DD', 'YYMMDD'], true);
    const asOfDate = asOfMoment.isValid() ? asOfMoment.format('YYYY-MM-DD') : latest.dateFull;

    return {
      latestIndex,
      latest,
      levNetSeries,
      amNetSeries,
      dealerNetSeries,
      levNetPercentiles,
      amNetPercentiles,
      dealerNetPercentiles,
      levNetZScores,
      previousWeek,
      previousFourWeeks,
      oiChange4w,
      asOfDate,
    };
  }, [trendSeries, rulesAssetConfig]);

  const anchoredPriceSignals = useMemo(() => {
    if (!signalsBase || !rulesAssetConfig) {
      return null;
    }
    const { latest, previousWeek, previousFourWeeks, asOfDate } = signalsBase;
    // Use absolute price change so ATR comparisons stay in price units.
    const priceReturn1w =
      latest.price != null && previousWeek?.price != null
        ? latest.price - previousWeek.price
        : null;
    const priceReturn4w =
      latest.price != null && previousFourWeeks?.price != null
        ? latest.price - previousFourWeeks.price
        : null;
    const dailySignals = buildDailySignals(
      trendPriceData,
      asOfDate,
      rulesAssetConfig.thresholds
    );
    return { asOfDate, priceReturn1w, priceReturn4w, dailySignals };
  }, [signalsBase, trendPriceData, rulesAssetConfig]);

  const livePriceSignals = useMemo(() => {
    if (!signalsBase || !rulesAssetConfig || !latestTrendPriceDate) {
      return null;
    }
    const asOfDate = latestTrendPriceDate;
    const priceReturn1w = computeDailyPriceReturn(asOfDate, 1);
    const priceReturn4w = computeDailyPriceReturn(asOfDate, 4);
    const dailySignals = buildDailySignals(
      trendPriceData,
      asOfDate,
      rulesAssetConfig.thresholds
    );
    return { asOfDate, priceReturn1w, priceReturn4w, dailySignals };
  }, [signalsBase, latestTrendPriceDate, trendPriceData, rulesAssetConfig]);

  const signalsContext = useMemo(() => {
    if (!signalsBase || !anchoredPriceSignals || !rulesAssetConfig) {
      return null;
    }
    const {
      latestIndex,
      latest,
      levNetSeries,
      amNetSeries,
      dealerNetSeries,
      levNetPercentiles,
      amNetPercentiles,
      dealerNetPercentiles,
      levNetZScores,
      previousWeek,
      previousFourWeeks,
      oiChange4w,
    } = signalsBase;
    const activePriceSignals =
      priceReferenceMode === 'live' && livePriceSignals ? livePriceSignals : anchoredPriceSignals;
    const peakAbs =
      maxAbsSparklinePoint?.rho != null ? Math.abs(maxAbsSparklinePoint.rho) : null;
    const peakLag = maxAbsSparklinePoint?.lag ?? null;
    const peakStable = maxAbsSparklinePoint
      ? hasConsistentSignAcrossLags(correlationSparklineData, maxAbsSparklinePoint.lag, 3)
      : false;

    return {
      LF_net: levNetSeries[latestIndex],
      AM_net: amNetSeries[latestIndex],
      Dealer_net: dealerNetSeries[latestIndex],
      LF_netPctOI: latest.levNetPct,
      AM_netPctOI: latest.assetNetPct,
      Dealer_netPctOI: latest.dealerNetPct,
      LF_net_z: levNetZScores[latestIndex],
      LF_net_percentile: levNetPercentiles[latestIndex],
      AM_net_percentile: amNetPercentiles[latestIndex],
      Dealer_net_percentile: dealerNetPercentiles[latestIndex],
      LF_deltaNet_1w:
        previousWeek != null ? levNetSeries[latestIndex] - levNetSeries[latestIndex - 1] : null,
      LF_deltaNet_4w:
        previousFourWeeks != null
          ? levNetSeries[latestIndex] - levNetSeries[latestIndex - 4]
          : null,
      OI: latest.openInterest,
      OI_change_4w: oiChange4w,
      price_close: activePriceSignals.dailySignals.priceClose,
      price_return_1w: activePriceSignals.priceReturn1w,
      price_return_4w: activePriceSignals.priceReturn4w,
      ATR_14: activePriceSignals.dailySignals.atr14,
      price_close_above_MA: activePriceSignals.dailySignals.priceCloseAboveMa,
      price_breakout_up: activePriceSignals.dailySignals.breakoutUp,
      corr_selectedLag_rho: correlationSummary.isSufficient
        ? correlationSummary.correlationValue
        : null,
      corr_curve: correlationSparklineData,
      corr_peak_abs: peakAbs,
      corr_peak_lag: peakLag,
      corr_peak_isStable: peakStable,
    };
  }, [
    signalsBase,
    anchoredPriceSignals,
    livePriceSignals,
    priceReferenceMode,
    correlationSummary,
    correlationSparklineData,
    maxAbsSparklinePoint,
    rulesAssetConfig,
  ]);

  const daysSinceCftcAsOf = useMemo(
    () => computeDaysSinceCftcAsOf(signalsBase?.asOfDate, Date.now()),
    [signalsBase?.asOfDate]
  );
  const liveDecay =
    priceReferenceMode === 'live' && daysSinceCftcAsOf != null
      ? computeLiveDecay(daysSinceCftcAsOf)
      : 1;
  const { alerts: activeAlerts, readiness: activeReadiness, badge: activeBadge } = useCftcSignals({
    context: signalsContext,
    assetClass: rulesAssetClass,
    symbolKey: selectedMarket,
    alertsEnabled,
    priceMode: priceReferenceMode,
    liveDecay,
  });
  const priceAsOfDate = selectPriceAsOfDate(
    priceReferenceMode,
    signalsBase?.asOfDate,
    livePriceSignals?.asOfDate
  );
  const priceAsOfLine = useMemo(() => {
    if (priceReferenceMode === 'live') {
      if (!latestTrendPricePoint) {
        return null;
      }
      const liveMoment = moment(latestTrendPricePoint.time);
      if (!liveMoment.isValid()) {
        return null;
      }
      return (
        'Price as-of: ' +
        liveMoment.format('dddd, MMMM D, YYYY') +
        ' ' +
        liveMoment.format('HH:mm') +
        ' ET (Live)'
      );
    }
    if (!priceAsOfDate) {
      return null;
    }
    const anchoredMoment = moment(priceAsOfDate, ['YYYY-MM-DD', 'YYMMDD'], true);
    if (!anchoredMoment.isValid()) {
      return null;
    }
    return (
      'Price as-of: ' +
      anchoredMoment.format('dddd, MMMM D, YYYY') +
      ' (CFTC as-of)'
    );
  }, [priceReferenceMode, latestTrendPricePoint, priceAsOfDate, signalsBase]);
  const liveModeBanner =
    priceReferenceMode === 'live'
      ? 'Live mode: Positioning is from the last CFTC report. Price signals update in real time. This view is not backtest-comparable.'
      : null;
  const agedPositioningWarning =
    priceReferenceMode === 'live' && daysSinceCftcAsOf != null && daysSinceCftcAsOf >= 7
      ? 'Positioning age: ' +
        daysSinceCftcAsOf +
        ' days since last CFTC as-of. Live signals may be less reliable until the next report.'
      : null;
  const correlationFootnote =
    priceReferenceMode === 'live'
      ? 'Correlations and historical stats are based on Anchored mode. Live mode is for real-time monitoring.'
      : null;

  const FOCUS_RING =
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-bg)]';

  const latestOpenInterest =
    trendSeries.length > 0 ? trendSeries[trendSeries.length - 1].openInterest : null;
  const openInterestLine =
    latestOpenInterest != null
      ? `Open Interest: ${formatNumber(latestOpenInterest)} contracts`
      : 'Open Interest: —';
  const readinessLabel = activeReadiness
    ? `TRADE READINESS: ${activeReadiness.score}/100 — ${activeReadiness.label}`
    : 'TRADE READINESS: —';
  const readinessTone = (() => {
    const label = activeReadiness?.label?.toLowerCase() || '';
    if (label.includes('trigger')) {
      return 'success';
    }
    if (label.includes('high') || label.includes('watch')) {
      return 'warning';
    }
    return 'neutral';
  })();
  const summaryOutcome = activeReadiness?.label
    ? `Opportunity ${activeReadiness.label.toLowerCase()}.`
    : 'Opportunity status unavailable.';
  const deskReadLines = [summaryOutcome];
  if (trendInsight) {
    deskReadLines.push(trendInsight);
  }

  const trendModeLabel =
    trendMode === 'short' ? 'Short Trend' : trendMode === 'long' ? 'Long Trend' : 'CFTC Snapshot';
  const trendRangeLabel = trendRangeDates
    ? `${trendRangeDates.start.format('MMM YYYY')} – ${trendRangeDates.end.format('MMM YYYY')}`
    : '';
  const selectedDateLabel = useMemo(() => {
    if (!selectedReportDate) {
      return '';
    }
    const date = moment(selectedReportDate, ['YYYY-MM-DD', 'YYMMDD'], true);
    return date.isValid() ? date.format('MMM D, YYYY') : selectedReportDate;
  }, [selectedReportDate]);
  const impactReportGeneratedLabel = useMemo(() => {
    if (!impactReportGeneratedAt) {
      return '';
    }
    return moment(impactReportGeneratedAt).format('MMM D, YYYY HH:mm');
  }, [impactReportGeneratedAt]);
  const latestTrendRow = trendSeries.length ? trendSeries[trendSeries.length - 1] : null;
  const yAxisLabel = useMemo(() => {
    if (positionView === 'net_pct_oi') {
      return '% of Open Interest';
    }
    if (positionView === 'zscore') {
      return 'Z-score';
    }
    return 'Contracts';
  }, [positionView]);
  const formatYAxisTick = (value) => formatViewValue(value, positionView);

  const handleMarketGroupChange = (event) => {
    const nextGroup = event.target.value;
    setSelectedMarketGroup(nextGroup);
    const group = contractGroups.find((entry) => entry.label === nextGroup);
    if (group?.options?.length) {
      setSelectedMarket(group.options[0]);
    }
  };
  const handleImpactGroupToggle = (groupId) => {
    setImpactReportGroupIdsDraft((prev) => {
      if (prev.includes(groupId)) {
        return prev.filter((id) => id !== groupId);
      }
      return prev.concat(groupId);
    });
  };
  const handleGenerateImpactReport = () => {
    setImpactReportRequest({
      scope: impactReportScopeDraft,
      lookbackWeeks: Number(impactReportLookbackDraft) || 4,
      groupIds: impactReportGroupIdsDraft.slice(),
    });
    setImpactReportGeneratedAt(Date.now());
  };

  const renderTrendTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) {
      return null;
    }
    const row = payload[0]?.payload;
    if (!row) {
      return null;
    }
    const openInterest = row.openInterest ?? 0;
    const deskRead =
      latestTrendRow && row.dateFull === latestTrendRow.dateFull ? trendInsight : null;
    const tooltipPayload = {
      dateISO: row.dateFull,
      price: row.price ?? null,
      openInterest,
      regimeLabel: activeBadge?.label,
      deskRead,
      actors: {
        dealer: {
          long: row.dealerLong,
          short: row.dealerShort,
          net: row.dealerNet,
          netPctOI: openInterest ? computeNetPctOI(row.dealerNet, openInterest) : 0,
        },
        assetManager: {
          long: row.assetLong,
          short: row.assetShort,
          net: row.assetNet,
          netPctOI: openInterest ? computeNetPctOI(row.assetNet, openInterest) : 0,
        },
        leveragedMoney: {
          long: row.levLong,
          short: row.levShort,
          net: row.levNet,
          netPctOI: openInterest ? computeNetPctOI(row.levNet, openInterest) : 0,
        },
      },
    };
    return (
      <CftcTooltipCard
        payload={tooltipPayload}
        actorMeta={{
          dealer: { color: PURCHASER_COLORS.Dealer },
          assetManager: { color: PURCHASER_COLORS.AssetManager },
          leveragedMoney: { color: PURCHASER_COLORS.LeveragedMoney },
        }}
      />
    );
  };

  const trendHelpNode = (
    <span
      className={styles.trendHelpIcon}
      aria-label="Dealer, Asset Manager, and Leveraged Funds definitions"
      tabIndex={0}
    >
      ?
      <span className={styles.trendHelpTooltip}>
        <span className={styles.trendHelpTitle}>Purchasers</span>
        <span className={styles.trendHelpItem}>
          <strong>Dealers</strong>: Banks and broker-dealers that make markets, provide liquidity,
          and hedge client flows (e.g., JPMorgan Chase, Goldman Sachs, Citigroup, Deutsche Bank).
        </span>
        <span className={styles.trendHelpItem}>
          <strong>Asset Managers</strong>: Long-term institutional investors managing client capital
          and hedging portfolios (e.g., BlackRock, Vanguard, PIMCO, Fidelity Investments).
        </span>
        <span className={styles.trendHelpItem}>
          <strong>Leveraged Funds</strong>: Professional investors that use leverage to trade futures
          and options for returns (e.g., hedge funds, CTAs, quant funds, proprietary trading
          firms).
        </span>
        <span className={styles.trendHelpTitle}>Desk use</span>
        <span className={styles.trendHelpItem}>
          Ignore raw longs. Net shows who’s actually leaning. Net % OI tells you if it matters.
          Z-score/Percentile flag crowded trades and squeeze risk. When Leveraged Funds are
          extreme, price usually moves against them.
        </span>
      </span>
    </span>
  );

  const priceReferenceControl = (
    <div className={styles.priceReferenceControl} data-onboard="price-reference">
      <span className={styles.priceReferenceLabel}>
        Price Reference
        <span className={`${styles.priceReferenceInfoIcon} ${FOCUS_RING}`} tabIndex={0}>
          ?
          <span className={styles.priceReferenceTooltip}>
            Anchored uses price as-of the last CFTC report date (backtest-consistent).
            <br />
            Live uses the latest available price with the last known CFTC positioning for
            real-time context.
          </span>
        </span>
      </span>
      <select
        className={styles.priceReferenceSelect}
        value={priceReferenceMode}
        onChange={(event) => setPriceReferenceMode(event.target.value)}
      >
        <option value="anchored">Anchored (CFTC date)</option>
        <option value="live">Live (latest price)</option>
      </select>
    </div>
  );

  const alertsButtonNode = (
    <div className="relative">
      <button
        type="button"
        className={`${styles.alertsButton} ${FOCUS_RING}`}
        data-onboard="alerts-pill"
        onClick={() => setAlertsPanelOpen((prev) => !prev)}
      >
        Alerts <span className={styles.alertsCount}>{activeAlerts.length}</span>
      </button>
      <AlertsPanel
        alerts={activeAlerts}
        isOpen={alertsPanelOpen}
        alertsEnabled={alertsEnabled}
        onToggleEnabled={(next) =>
          setAlertsEnabledBySymbol((prev) => ({ ...prev, [selectedMarket]: next }))
        }
        onClose={() => setAlertsPanelOpen(false)}
      />
    </div>
  );

  const trendControlsNode = (
    <div className={styles.trendControls}>
      <div className={styles.trendMenu} ref={trendSettingsRef}>
        <button
          type="button"
          className={`${styles.trendMenuButton} ${FOCUS_RING}`}
          aria-label="Chart date and interval settings"
          onClick={() => setTrendSettingsOpen((prev) => !prev)}
        >
          ⋯
        </button>
        {trendSettingsOpen && (
          <div className={styles.trendMenuPanel}>
            <div className={styles.trendMenuGroup}>
              <span className={styles.trendMenuTitle}>Source</span>
              <select
                className={styles.trendSelect}
                value={tvSource}
                onChange={(event) =>
                  setTvSourceByMarket((prev) => ({
                    ...prev,
                    [selectedMarket]: event.target.value,
                  }))
                }
              >
                {TRADINGVIEW_SOURCES.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.trendMenuGroup}>
              <span className={styles.trendMenuTitle}>Symbol</span>
              <input
                className={styles.trendSelect}
                type="text"
                key={`tv-symbol-${selectedMarket}-trend`}
                value={tvSymbolOverrideByMarket[selectedMarket] ?? tvSymbolDerived}
                onChange={(event) =>
                  setTvSymbolOverrideByMarket((prev) => {
                    const next = { ...prev };
                    const nextValue = event.target.value.trim();
                    if (!nextValue) {
                      delete next[selectedMarket];
                    } else {
                      next[selectedMarket] = nextValue;
                    }
                    return next;
                  })}
              />
            </div>
            <div className={styles.trendMenuGroup}>
              <span className={styles.trendMenuTitle}>Start</span>
              <select
                className={styles.trendSelect}
                value={trendStartYear}
                onChange={(event) => setTrendStartYear(event.target.value)}
              >
                {trendYearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <select
                className={styles.trendSelect}
                value={trendStartMonth}
                onChange={(event) => setTrendStartMonth(event.target.value)}
              >
                {trendStartMonthOptions.map((month) => (
                  <option key={month} value={month}>
                    {moment(month, 'MM').format('MMM')}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.trendMenuGroup}>
              <span className={styles.trendMenuTitle}>End</span>
              <select
                className={styles.trendSelect}
                value={trendEndYear}
                onChange={(event) => setTrendEndYear(event.target.value)}
              >
                {trendYearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <select
                className={styles.trendSelect}
                value={trendEndMonth}
                onChange={(event) => setTrendEndMonth(event.target.value)}
              >
                {trendEndMonthOptions.map((month) => (
                  <option key={month} value={month}>
                    {moment(month, 'MM').format('MMM')}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.trendMenuGroup}>
              <span className={styles.trendMenuTitle}>Interval</span>
              <select
                className={styles.trendSelect}
                value={tvInterval}
                onChange={(event) =>
                  setTvIntervalByMarket((prev) => ({
                    ...prev,
                    [selectedMarket]: event.target.value,
                  }))
                }
              >
                {TRADINGVIEW_INTERVALS.map((interval) => (
                  <option key={interval.value} value={interval.value}>
                    {interval.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.trendMenuGroup}>
              <span className={styles.trendMenuTitle}>View</span>
              <select
                className={styles.trendSelect}
                value={positionView}
                onChange={(event) => setPositionView(event.target.value)}
              >
                {POSITION_VIEW_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.trendMenuGroup}>
              <span className={styles.trendMenuTitle}>Extremes</span>
              <select
                className={styles.trendSelect}
                value={extremesMode}
                onChange={(event) => {
                  setExtremesMode(event.target.value);
                  setExtremesTouched(true);
                }}
              >
                <option value="off">Off</option>
                <option value="1y">1Y</option>
                <option value="3y">3Y</option>
                <option value="both">1Y+3Y</option>
              </select>
            </div>
            <div className={styles.trendMenuGroup}>
              <span className={styles.trendMenuTitle}>Overlays</span>
              <label className={styles.trendToggle}>
                <input
                  type="checkbox"
                  checked={showOpenInterest}
                  onChange={(event) => setShowOpenInterest(event.target.checked)}
                />
                Show Open Interest
              </label>
              <label className={styles.trendToggle}>
                <input
                  type="checkbox"
                  checked={showWeeklyMarkers}
                  onChange={(event) => setShowWeeklyMarkers(event.target.checked)}
                />
                Show weekly markers
              </label>
              <label className={styles.trendToggle}>
                <input
                  type="checkbox"
                  checked={grossActivityBadgeEnabled}
                  onChange={(event) => setGrossActivityBadgeEnabled(event.target.checked)}
                />
                Show gross activity badge
              </label>
            </div>
            <div className={styles.trendMenuGroup}>
              <span className={styles.trendMenuTitle}>Range</span>
              <select
                className={styles.trendSelect}
                value={tvRange}
                onChange={(event) => {
                  const nextRange = event.target.value;
                  setTvRangeByMarket((prev) => {
                    const next = { ...prev };
                    if (nextRange === tvRangeDefault) {
                      delete next[selectedMarket];
                    } else {
                      next[selectedMarket] = nextRange;
                    }
                    return next;
                  });
                }}
              >
                {TRADINGVIEW_RANGES.map((range) => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const headerControlsNode = (
    <div className={styles.chartControlsRow}>
      <button
        type="button"
        className={`${styles.signalsTourLink} ${styles.signalsTourLinkLeft} ${FOCUS_RING}`}
        onClick={handleSignalsOnboardingReplay}
      >
        <HiOutlineSparkles className={styles.sparklesIcon} />
        Guided walkthrough
      </button>
      {priceReferenceControl}
      {trendControlsNode}
    </div>
  );

  const cftcSummaryGuideLink = (
    <button
      type="button"
      className={`${styles.signalsTourLink} ${styles.signalsTourLinkLeft} ${FOCUS_RING}`}
      onClick={handleCftcSummaryOnboardingReplay}
    >
      <HiOutlineSparkles className={styles.sparklesIcon} />
      CFTC Summary Guided Walkthrough
    </button>
  );


  const summaryPillsNode = (
    <>
      <span className={styles.summaryMuted}>{openInterestLine}</span>
      {activeBadge && (
        <span data-onboard="regime-badge">
          <RegimePill label={activeBadge.label} />
        </span>
      )}
      {grossActivityBadgeEnabled && grossActivity && (
        <GrossActivityBadge
          level={grossActivity.label}
          insufficientHistory={grossActivity.insufficientHistory}
        />
      )}
      {trendRangeLabel && <span className={styles.summaryMuted}>{trendRangeLabel}</span>}
    </>
  );

  const summaryCardNode = (
    <SummaryCard className={styles.summaryCardContent}>
      <div className={styles.summaryHeaderRow}>
        <h2 className={styles.sectionTitle}>Summary</h2>
        <div className={styles.pillsRow}>{summaryPillsNode}</div>
      </div>
      <div className={styles.rowBetween}>
        {activeReadiness ? (
          <ReadinessBadge
            readiness={activeReadiness}
            scale={readinessScale}
            definitions={readinessDefinitions}
          />
        ) : (
          <StatusPill label={readinessLabel} tone={readinessTone} />
        )}
        {alertsButtonNode}
      </div>
      <div className={styles.summaryRegimeRow}>
        {activeBadge && <RegimePill label={activeBadge.label} />}
        <span className={styles.summaryStrong}>{summaryOutcome}</span>
      </div>
      {(activeBadge?.tooltip || liveModeBanner || agedPositioningWarning) && (
        <ul className={styles.summaryBulletList}>
          {activeBadge?.tooltip && (
            <li className={styles.summaryBulletItem}>
              <span className={styles.summaryBulletDot} />
              <span className={styles.summaryText}>
                <span className={styles.summaryBulletLabel}>Regime note:</span>{' '}
                {activeBadge.tooltip}
              </span>
            </li>
          )}
          {liveModeBanner && (
            <li className={styles.summaryBulletItem}>
              <span className={styles.summaryBulletDot} />
              <span className={styles.summaryText}>
                <span className={styles.summaryBulletLabel}>Live mode:</span> {liveModeBanner}
              </span>
            </li>
          )}
          {agedPositioningWarning && (
            <li className={styles.summaryBulletItem}>
              <span className={styles.summaryBulletDot} />
              <span className={`${styles.summaryText} ${styles.summaryWarning}`}>
                <span className={styles.summaryBulletLabel}>Positioning age:</span>{' '}
                {agedPositioningWarning}
              </span>
            </li>
          )}
        </ul>
      )}
      {trendInsight && (
        <div className={styles.summaryText}>{trendInsight}</div>
      )}
      {priceAsOfLine && (
        <div className={styles.summaryMuted}>{priceAsOfLine}</div>
      )}
      <div className={styles.summaryMuted}>
        CFTC positioning is weekly: as-of Tuesday, published Friday.
      </div>
    </SummaryCard>
  );

  const deskReadItems = deskReadLines.filter(Boolean);
  const deskReadCardNode = null;

  const positioningChartBadgeNode = (
    <div className={styles.rowWrap}>
      {trendHelpNode}
      {activeBadge && (
        <span data-onboard="position-trends-regime">
          <RegimePill label={activeBadge.label} />
        </span>
      )}
    </div>
  );

  const netPositioningHeaderRight = (
    <div className={styles.rowWrap} data-onboard="position-trends-view">
      <span className={styles.correlationMenuLabel}>
        View: {positionViewLabel}
        <span className={`${styles.correlationInfoIcon} ${FOCUS_RING}`} tabIndex={0}>
          ?
          <span className={styles.correlationInfoTooltip}>
            <span className={styles.correlationInfoTitle}>{positionViewLabel}</span>
            <span className={styles.correlationInfoText}>
              {POSITION_VIEW_DESCRIPTIONS[positionView] || 'View definition unavailable.'}
            </span>
          </span>
        </span>
      </span>
    </div>
  );

  const netPositioningCardTitle = `Position Trends: ${positionViewLabel}`;

  const positionTrendsGuideLink = (
    <button
      type="button"
      className={`${styles.signalsTourLink} ${FOCUS_RING}`}
      onClick={handlePositionTrendsOnboardingReplay}
    >
      <HiOutlineSparkles className={styles.sparklesIcon} />
      Position Trend Guided Walkthrough
    </button>
  );

  const trendSummaryText = useMemo(() => {
    if (!trendSeries.length || trendSeries.length < 2) {
      return 'Not enough history to summarize recent positioning shifts.';
    }
    const last = trendSeries[trendSeries.length - 1];
    const prev = trendSeries[trendSeries.length - 2];
    const viewLabel =
      positionView === 'short'
        ? 'short positions'
        : positionView === 'long'
        ? 'long positions'
        : positionView === 'net_pct_oi'
        ? 'net % of open interest'
        : 'net positioning';
    const buildLine = (meta) => {
      const lastValue =
        positionView === 'long'
          ? last[meta.longKey] ?? 0
          : positionView === 'short'
          ? last[meta.shortKey] ?? 0
          : last[meta.netKey] ?? 0;
      const prevValue =
        positionView === 'long'
          ? prev[meta.longKey] ?? 0
          : positionView === 'short'
          ? prev[meta.shortKey] ?? 0
          : prev[meta.netKey] ?? 0;
      const change = lastValue - prevValue;
      const pct = prevValue ? (change / Math.abs(prevValue)) * 100 : null;
      const direction = change >= 0 ? 'up' : 'down';
      const pctText = pct == null ? '—' : `${Math.abs(pct).toFixed(1)}%`;
      const shareValue =
        positionView === 'long' || positionView === 'short'
          ? computeNetPctOI(lastValue, last.openInterest)
          : last[meta.netPctKey];
      const shareText = shareValue == null ? '—' : `${shareValue.toFixed(1)}%`;
      return `${meta.label} ${viewLabel} ${direction} ${formatNumber(Math.abs(change))} (${pctText}) vs prior week. Share of OI: ${shareText}.`;
    };
    return [
      {
        label: 'Asset Managers',
        color: PURCHASER_COLORS.AssetManager,
        netKey: 'assetNet',
        netPctKey: 'assetNetPct',
        longKey: 'assetLong',
        shortKey: 'assetShort',
      },
      {
        label: 'Leveraged Funds',
        color: PURCHASER_COLORS.LeveragedMoney,
        netKey: 'levNet',
        netPctKey: 'levNetPct',
        longKey: 'levLong',
        shortKey: 'levShort',
      },
      {
        label: 'Dealers',
        color: PURCHASER_COLORS.Dealer,
        netKey: 'dealerNet',
        netPctKey: 'dealerNetPct',
        longKey: 'dealerLong',
        shortKey: 'dealerShort',
      },
    ].map((item) => ({
      color: item.color,
      text: buildLine(item),
    }));
  }, [trendSeries, positionView]);

  const positioningChartSummaryNode = useMemo(() => {
    if (typeof trendSummaryText === 'string') {
      return trendSummaryText;
    }
    if (!trendSummaryText?.length) {
      return null;
    }
    return (
      <span className={styles.chartSummaryItems}>
        {trendSummaryText.map((item, index) => (
          <span
            key={item.text}
            className={styles.chartSummaryItem}
            style={{ color: item.color }}
          >
            <span className={styles.chartSummaryDot} />
            <span>{item.text}</span>
            {index < trendSummaryText.length - 1 ? ' ' : ''}
          </span>
        ))}
      </span>
    );
  }, [trendSummaryText]);

  const positioningChartNode = trendSeries.length ? (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={trendSeries} margin={{ top: 6, right: 20, bottom: 6, left: 10 }}>
        <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" />
        <XAxis
          dataKey="dateFull"
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 10, fontWeight: 600, fill: 'var(--color-text-muted)' }}
          tickFormatter={(value) => moment(value).format('MM/DD')}
        />
        <YAxis
          yAxisId="position"
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 10, fontWeight: 600, fill: 'var(--color-text-muted)' }}
          tickFormatter={formatYAxisTick}
          label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', dx: -12 }}
        />
        {showOpenInterest && (
          <YAxis
            yAxisId="oi"
            orientation="right"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 10, fontWeight: 600, fill: 'var(--color-text-muted)' }}
            tickFormatter={formatNumber}
            label={{ value: 'Open Interest', angle: 90, position: 'insideRight', dx: 12 }}
          />
        )}
        <Tooltip content={renderTrendTooltip} />
        <Legend verticalAlign="top" wrapperStyle={{ paddingBottom: 6 }} />
        {showWeeklyMarkers &&
          trendSeries.map((row) => (
            <ReferenceLine
              key={`marker-${row.dateFull}`}
              x={row.dateFull}
              stroke="var(--color-border)"
              strokeDasharray="2 2"
              yAxisId="position"
            />
          ))}
        {showExtremes1y && (
          <>
            <Area
              dataKey="dealerBand1yLower"
              stackId="dealerBand1y"
              stroke="none"
              fill="none"
              yAxisId="position"
              isAnimationActive={false}
              legendType="none"
            />
            <Area
              dataKey="dealerBand1yRange"
              stackId="dealerBand1y"
              stroke="none"
              fill={toRgba(PURCHASER_COLORS.Dealer, 0.16)}
              fillOpacity={0.16}
              yAxisId="position"
              isAnimationActive={false}
              name="1Y range (P10-P90)"
            />
            <Area
              dataKey="assetBand1yLower"
              stackId="assetBand1y"
              stroke="none"
              fill="none"
              yAxisId="position"
              isAnimationActive={false}
              legendType="none"
            />
            <Area
              dataKey="assetBand1yRange"
              stackId="assetBand1y"
              stroke="none"
              fill={toRgba(PURCHASER_COLORS.AssetManager, 0.12)}
              fillOpacity={0.12}
              yAxisId="position"
              isAnimationActive={false}
              legendType="none"
            />
            <Area
              dataKey="levBand1yLower"
              stackId="levBand1y"
              stroke="none"
              fill="none"
              yAxisId="position"
              isAnimationActive={false}
              legendType="none"
            />
            <Area
              dataKey="levBand1yRange"
              stackId="levBand1y"
              stroke="none"
              fill={toRgba(PURCHASER_COLORS.LeveragedMoney, 0.12)}
              fillOpacity={0.12}
              yAxisId="position"
              isAnimationActive={false}
              name="1Y range (P10-P90)"
            />
          </>
        )}
        {showExtremes3y && (
          <>
            <Area
              dataKey="dealerBand3yLower"
              stackId="dealerBand3y"
              stroke="none"
              fill="none"
              yAxisId="position"
              isAnimationActive={false}
              legendType="none"
            />
            <Area
              dataKey="dealerBand3yRange"
              stackId="dealerBand3y"
              stroke="none"
              fill={toRgba(PURCHASER_COLORS.Dealer, 0.08)}
              fillOpacity={0.08}
              yAxisId="position"
              isAnimationActive={false}
              name="3Y range (P5-P95)"
            />
            <Area
              dataKey="assetBand3yLower"
              stackId="assetBand3y"
              stroke="none"
              fill="none"
              yAxisId="position"
              isAnimationActive={false}
              legendType="none"
            />
            <Area
              dataKey="assetBand3yRange"
              stackId="assetBand3y"
              stroke="none"
              fill={toRgba(PURCHASER_COLORS.AssetManager, 0.06)}
              fillOpacity={0.06}
              yAxisId="position"
              isAnimationActive={false}
              legendType="none"
            />
            <Area
              dataKey="levBand3yLower"
              stackId="levBand3y"
              stroke="none"
              fill="none"
              yAxisId="position"
              isAnimationActive={false}
              legendType="none"
            />
            <Area
              dataKey="levBand3yRange"
              stackId="levBand3y"
              stroke="none"
              fill={toRgba(PURCHASER_COLORS.LeveragedMoney, 0.06)}
              fillOpacity={0.06}
              yAxisId="position"
              isAnimationActive={false}
              legendType="none"
            />
          </>
        )}
        {PURCHASER_GROUPS.map((group) => (
          <Line
            key={group.id}
            type="monotone"
            dataKey={group.id}
            yAxisId="position"
            stroke={PURCHASER_COLORS[group.id] || '#8ab4f8'}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
        ))}
        {showOpenInterest && (
          <Line
            type="monotone"
            dataKey="openInterest"
            yAxisId="oi"
            stroke="var(--color-warning)"
            strokeWidth={1}
            dot={false}
            name="Open Interest"
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  ) : (
    <p className={styles.chartHint}>No positioning data available.</p>
  );

  const snapshotSummaryNode = (
    <div
      className={styles.panelCard}
      data-onboard="position-snapshot"
      data-testid="cftc-position-snapshot"
    >
      <div className={styles.rowBetween}>
        <div className={styles.panelTitle}>Position Snapshot</div>
        <div className={styles.summaryMuted}>{openInterestLine}</div>
      </div>
      <div className={styles.panelBody}>
        <div className={styles.snapshotChartHeader}>
          <div className={styles.snapshotChartTitle}>
            Positions by participant
            <span className={`${styles.correlationInfoIcon} ${FOCUS_RING}`} tabIndex={0}>
              ?
              <span className={styles.correlationInfoTooltip}>
                <span className={styles.correlationInfoTitle}>How to read</span>
                <span className={styles.correlationInfoText}>
                  Shows long, short, and spreading contracts by participant category for the selected
                  report date.
                </span>
              </span>
            </span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={snapshotRows} margin={{ top: 6, right: 20, left: 10, bottom: 6 }}>
            <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
              interval={0}
            />
            <YAxis tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }} />
            <Tooltip
              formatter={(value) => formatNumber(value)}
              contentStyle={{
                background: 'var(--color-bg-panel)',
                borderColor: 'var(--color-border)',
                color: 'var(--color-text)',
              }}
            />
            <Legend />
            <Bar dataKey="long" name="Long" fill={PURCHASER_COLORS.Dealer} />
            <Bar dataKey="short" name="Short" fill={PURCHASER_COLORS.AssetManager} />
            <Bar dataKey="spread" name="Spreading" fill={PURCHASER_COLORS.LeveragedMoney} />
          </BarChart>
        </ResponsiveContainer>
        {snapshotPctOISeries && (
          <div
            className={styles.snapshotDonutWrap}
            key={`snapshot-donut-${snapshotTab}-${selectedReportDate || ''}`}
            ref={summaryDonutRef}
            data-testid="cftc-oi-percent-donuts"
          >
            <div className={styles.panelTitleSmall}>
              Percent of Open Interest by Category
              <span className={`${styles.correlationInfoIcon} ${FOCUS_RING}`} tabIndex={0}>
                ?
                <span className={styles.correlationInfoTooltip}>
                  <span className={styles.correlationInfoTitle}>How to read</span>
                  <span className={styles.correlationInfoText}>
                    Each donut shows the share of total open interest by participant category.
                  </span>
                </span>
              </span>
            </div>
            <div className={styles.snapshotDonutGrid}>
              <div className={styles.snapshotDonutCard}>
                <div className={styles.snapshotDonutLabel}>
                  Long % OI
                  <span className={`${styles.correlationInfoIcon} ${FOCUS_RING}`} tabIndex={0}>
                    ?
                    <span className={styles.correlationInfoTooltip}>
                      <span className={styles.correlationInfoTitle}>How to read</span>
                      <span className={styles.correlationInfoText}>
                        Shows each category’s long positions as a percent of total open interest.
                      </span>
                    </span>
                  </span>
                </div>
                <Plot
                  key={`donut-long-${snapshotTab}-${selectedReportDate || ''}`}
                  data={[
                    {
                      type: 'pie',
                      hole: 0.55,
                      values: snapshotPctOISeries.longSeries.map((slice) => slice.value),
                      labels: snapshotPctOISeries.longSeries.map((slice) => slice.label),
                      textinfo: 'percent',
                      textposition: 'inside',
                      hovertemplate: '%{label}: %{percent}<extra></extra>',
                      marker: {
                        colors: snapshotPctOISeries.longSeries.map((slice) => {
                          if (slice.label === 'Dealer') return PURCHASER_COLORS.Dealer;
                          if (slice.label === 'Asset Manager') return PURCHASER_COLORS.AssetManager;
                          if (slice.label === 'Leveraged Funds')
                            return PURCHASER_COLORS.LeveragedMoney;
                          return 'var(--color-text-muted)';
                        }),
                      },
                    },
                  ]}
                  layout={{
                    height: 220,
                    margin: { t: 6, r: 6, b: 6, l: 6 },
                    paper_bgcolor: plotlyPanelBg,
                    plot_bgcolor: plotlyPanelBg,
                    font: {
                      color: plotlyTextColor,
                      size: 16,
                      family: 'Inter SemiBold, Inter, system-ui, sans-serif',
                    },
                    hoverlabel: {
                      font: {
                        size: 16,
                        family: 'Inter SemiBold, Inter, system-ui, sans-serif',
                        color: plotlyTextColor,
                      },
                    },
                    showlegend: false,
                  }}
                  config={{ displayModeBar: false, responsive: true }}
                  useResizeHandler
                  style={{ width: '100%', height: '100%' }}
                />
              </div>
              <div className={styles.snapshotDonutCard}>
                <div className={styles.snapshotDonutLabel}>
                  Short % OI
                  <span className={`${styles.correlationInfoIcon} ${FOCUS_RING}`} tabIndex={0}>
                    ?
                    <span className={styles.correlationInfoTooltip}>
                      <span className={styles.correlationInfoTitle}>How to read</span>
                      <span className={styles.correlationInfoText}>
                        Shows each category’s short positions as a percent of total open interest.
                      </span>
                    </span>
                  </span>
                </div>
                <Plot
                  key={`donut-short-${snapshotTab}-${selectedReportDate || ''}`}
                  data={[
                    {
                      type: 'pie',
                      hole: 0.55,
                      values: snapshotPctOISeries.shortSeries.map((slice) => slice.value),
                      labels: snapshotPctOISeries.shortSeries.map((slice) => slice.label),
                      textinfo: 'percent',
                      textposition: 'inside',
                      hovertemplate: '%{label}: %{percent}<extra></extra>',
                      marker: {
                        colors: snapshotPctOISeries.shortSeries.map((slice) => {
                          if (slice.label === 'Dealer') return PURCHASER_COLORS.Dealer;
                          if (slice.label === 'Asset Manager') return PURCHASER_COLORS.AssetManager;
                          if (slice.label === 'Leveraged Funds')
                            return PURCHASER_COLORS.LeveragedMoney;
                          return 'var(--color-text-muted)';
                        }),
                      },
                    },
                  ]}
                  layout={{
                    height: 220,
                    margin: { t: 6, r: 6, b: 6, l: 6 },
                    paper_bgcolor: plotlyPanelBg,
                    plot_bgcolor: plotlyPanelBg,
                    font: {
                      color: plotlyTextColor,
                      size: 16,
                      family: 'Inter SemiBold, Inter, system-ui, sans-serif',
                    },
                    hoverlabel: {
                      font: {
                        size: 16,
                        family: 'Inter SemiBold, Inter, system-ui, sans-serif',
                        color: plotlyTextColor,
                      },
                    },
                    showlegend: false,
                  }}
                  config={{ displayModeBar: false, responsive: true }}
                  useResizeHandler
                  style={{ width: '100%', height: '100%' }}
                />
              </div>
            </div>
          </div>
        )}
        {snapshotChangeSeries && (
          <div
            className={styles.snapshotChangeCharts}
            data-onboard="change-charts"
            data-testid="cftc-change-contracts"
          >
            <div className={styles.panelTitleSmall}>Change From (Contracts)</div>
            <div className={styles.snapshotChangeGrid}>
              <div className={styles.snapshotChangeCard}>
                <div className={styles.snapshotChangeTitle}>
                  Diverging Bar
                  <span className={`${styles.correlationInfoIcon} ${FOCUS_RING}`} tabIndex={0}>
                    ?
                    <span className={styles.correlationInfoTooltip}>
                      <span className={styles.correlationInfoTitle}>How to read</span>
                      <span className={styles.correlationInfoText}>
                        Bars show net change (ΔLong − ΔShort). Positive = net buying, negative = net
                        selling. Hover a bar to see ΔLong/ΔShort details.
                      </span>
                    </span>
                  </span>
                </div>
                <Plot
                  data={[
                    {
                      type: 'bar',
                      orientation: 'h',
                      y: snapshotChangeSeries.map((row) => row.label),
                      x: snapshotChangeSeries.map((row) => row.netChange),
                      marker: {
                        color: snapshotChangeSeries.map((row) => {
                          if (row.label === 'Dealer') return PURCHASER_COLORS.Dealer;
                          if (row.label === 'Asset Manager') return PURCHASER_COLORS.AssetManager;
                          if (row.label === 'Leveraged Funds') return PURCHASER_COLORS.LeveragedMoney;
                          if (row.label === 'Other Reportables') return plotlyWarningColor;
                          if (row.label === 'Nonreportable') return plotlyAccentColor;
                          return 'var(--color-text-muted)';
                        }),
                      },
                      customdata: snapshotChangeSeries.map((row) => [
                        row.longChange,
                        row.shortChange,
                        row.netChange,
                      ]),
                      hovertemplate:
                        '%{y}<br>' +
                        'ΔLong: %{customdata[0]:,}<br>' +
                        'ΔShort: %{customdata[1]:,}<br>' +
                        'Net: %{customdata[2]:,}<extra></extra>',
                    },
                  ]}
                  layout={{
                    height: 220,
                    margin: { t: 10, r: 10, b: 28, l: 60 },
                    paper_bgcolor: plotlyPanelBg,
                    plot_bgcolor: plotlyPanelBg,
                    font: {
                      color: plotlyTextColor,
                      size: 16,
                      family: 'Inter SemiBold, Inter, system-ui, sans-serif',
                    },
                    xaxis: {
                      zerolinecolor: 'var(--color-border)',
                      gridcolor: 'var(--color-border)',
                    },
                    yaxis: {
                      automargin: true,
                    },
                  }}
                  config={{ displayModeBar: false, responsive: true }}
                  useResizeHandler
                  style={{ width: '100%', height: '100%' }}
                />
              </div>
              <div
                className={styles.snapshotChangeCard}
                data-testid="cftc-waterfall-net-change"
              >
                <div className={styles.snapshotChangeTitle}>
                  Waterfall (Net Change)
                  <span className={`${styles.correlationInfoIcon} ${FOCUS_RING}`} tabIndex={0}>
                    ?
                    <span className={styles.correlationInfoTooltip}>
                      <span className={styles.correlationInfoTitle}>How to read</span>
                      <span className={styles.correlationInfoText}>
                        Each step adds that category’s net change. The final “Total” bar is the sum
                        of all net changes for the week.
                      </span>
                    </span>
                  </span>
                </div>
                <Plot
                  data={[
                    {
                      type: 'waterfall',
                      orientation: 'v',
                      x: snapshotChangeSeries.map((row) => row.label).concat(['Total']),
                      y: snapshotChangeSeries.map((row) => row.netChange).concat([
                        snapshotChangeSeries.reduce((sum, row) => sum + row.netChange, 0),
                      ]),
                      customdata: snapshotChangeSeries
                        .map((row) => [row.longChange, row.shortChange, row.netChange])
                        .concat([[null, null, snapshotChangeSeries.reduce((sum, row) => sum + row.netChange, 0)]]),
                      measure: snapshotChangeSeries
                        .map(() => 'relative')
                        .concat(['total']),
                      increasing: { marker: { color: 'var(--color-success)' } },
                      decreasing: { marker: { color: 'var(--color-danger)' } },
                      totals: { marker: { color: 'var(--color-accent)' } },
                      hovertemplate:
                        '%{x}<br>' +
                        'ΔLong: %{customdata[0]:,}<br>' +
                        'ΔShort: %{customdata[1]:,}<br>' +
                        'Net: %{customdata[2]:,}<extra></extra>',
                    },
                  ]}
                  layout={{
                    height: 220,
                    margin: { t: 10, r: 10, b: 40, l: 40 },
                    paper_bgcolor: plotlyPanelBg,
                    plot_bgcolor: plotlyPanelBg,
                    font: {
                      color: plotlyTextColor,
                      size: 16,
                      family: 'Inter SemiBold, Inter, system-ui, sans-serif',
                    },
                    xaxis: {
                      automargin: true,
                    },
                    yaxis: {
                      zerolinecolor: 'var(--color-border)',
                      gridcolor: 'var(--color-border)',
                    },
                  }}
                  config={{ displayModeBar: false, responsive: true }}
                  useResizeHandler
                  style={{ width: '100%', height: '100%' }}
                />
              </div>
            </div>
          </div>
        )}
        <div className={styles.tableScroller}>
          <table className={styles.purchaserTable} data-testid="cftc-participant-table">
            <thead>
              <tr>
                <th>Participant</th>
                <th>Long</th>
                <th>Short</th>
                <th>Spreading</th>
                <th data-testid="cftc-net-positioning">Net</th>
                <th>Net % OI</th>
              </tr>
            </thead>
            <tbody>
              {snapshotRows.map((row) => (
                <tr key={row.id}>
                  <td>{row.label}</td>
                  <td>{formatNumber(row.long)}</td>
                  <td>{formatNumber(row.short)}</td>
                  <td>{formatNumber(row.spread)}</td>
                  <td>{formatSignedNumber(row.net)}</td>
                  <td>{formatPercent(row.netPct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={styles.summaryMuted} data-testid="cftc-disclaimer">
          CFTC positioning is weekly: as-of Tuesday, published Friday.
        </div>
      </div>
    </div>
  );

  const correlationValueDisplay =
    correlationSummary.isSufficient && correlationSummary.correlationValue != null
      ? correlationSummary.correlationValue.toFixed(2)
      : '—';
  const correlationStrengthLabel = correlationSummary.strengthBucket
    ? `${correlationSummary.strengthBucket.label} (${correlationSummary.strengthBucket.range})`
    : 'Insufficient data';

  const correlationSummaryCard = (
    <SummaryCard className={styles.summaryCardCompact}>
      <div className={styles.correlationStatRow}>
        <div className={styles.correlationStatInline}>
          <span className={styles.correlationStatInlineLabel}>ρ:</span>
          <span ref={correlationRhoRef} className={styles.correlationStatInlineValue}>
            {correlationValueDisplay}
          </span>
        </div>
        <span className={styles.correlationStatDivider}>|</span>
        <span className={styles.correlationStatMeta}>N={correlationSummary.sampleSize}</span>
        <span className={styles.correlationStatDivider}>|</span>
        <span className={styles.correlationStatStrength}>{correlationStrengthLabel}</span>
      </div>
      <div className={styles.correlationText}>{correlationSummary.lagLabel}</div>
      <div className={styles.correlationTextSmall}>{correlationSummary.interpretationText}</div>
      <div className={styles.correlationTextSmall}>{correlationSummary.lagExplanation}</div>
      {correlationLag === 0 && (
        <div className={styles.correlationWarning}>
          Same-date correlations can be spurious; use lead/lag for insight.
        </div>
      )}
      {correlationFootnote && (
        <div ref={correlationFooterRef} className={styles.correlationFootnote}>
          {correlationFootnote}
        </div>
      )}
      <div className={styles.correlationHelpLinks}>
        <button
          type="button"
          className={`${styles.correlationTourLink} ${FOCUS_RING}`}
          onClick={() => setShowRhoHelpModal(true)}
        >
          ρ help guide
        </button>
      </div>
    </SummaryCard>
  );

  const positionVsPriceGuideLink = (
    <button
      type="button"
      className={`${styles.correlationTourLink} ${FOCUS_RING}`}
      onClick={handlePositioningTourRestart}
    >
      <HiOutlineSparkles className={styles.sparklesIcon} />
      Position vs Price Guided Walkthrough
    </button>
  );

  const rhoHelpContent = (
    <>
      <div className={styles.helpGuideTitle}>
        Help Guide: Understanding ρ (Rho) in Positioning vs. Price
      </div>
      <div className={styles.helpGuideSection}>
        <div className={styles.helpGuideHeading}>What is ρ (rho)?</div>
        <p>
          ρ (rho) measures the historical relationship between changes in CFTC positioning and
          subsequent price returns over a selected time horizon.
        </p>
        <p>In simple terms:</p>
        <div className={styles.helpGuideQuote}>
          ρ shows whether positioning changes have historically aligned with future price movement
          — and how consistently.
        </div>
        <p>It does not indicate trade signals or predictions.</p>
      </div>
      <div className={styles.helpGuideSection}>
        <div className={styles.helpGuideHeading}>What rho tells you</div>
        <p>Rho answers one specific question:</p>
        <div className={styles.helpGuideQuote}>
          When positioning changed in the past, did price tend to move afterward — and in which
          direction?
        </div>
        <p>It helps assess whether positioning has acted as:</p>
        <ul className={styles.helpGuideList}>
          <li>A structural driver</li>
          <li>A confirming factor</li>
          <li>Or largely noise</li>
        </ul>
      </div>
      <div className={styles.helpGuideSection}>
        <div className={styles.helpGuideHeading}>What rho does not mean</div>
        <p>ρ should not be interpreted as:</p>
        <ul className={styles.helpGuideList}>
          <li>❌ A trading signal</li>
          <li>❌ A forecast of future price</li>
          <li>❌ A measure of trade edge</li>
          <li>❌ Confirmation of current market direction</li>
        </ul>
        <p>A high or low rho does not imply that positioning will matter now.</p>
      </div>
      <div className={styles.helpGuideSection}>
        <div className={styles.helpGuideHeading}>How rho is calculated (conceptual)</div>
        <ol className={styles.helpGuideList}>
          <li>Measure the change in net positioning</li>
          <li>Measure the future price return over the selected horizon</li>
          <li>Repeat across the historical sample</li>
          <li>Compute the correlation between positioning changes and forward returns</li>
        </ol>
        <p>The result is ρ (rho).</p>
      </div>
      <div className={styles.helpGuideSection}>
        <div className={styles.helpGuideHeading}>Interpreting rho values</div>
        <p>Use rho as a confidence context, not a decision rule.</p>
        <ul className={styles.helpGuideList}>
          <li>|ρ| &lt; 0.20 — Very weak relationship (noise-dominated)</li>
          <li>0.20 – 0.35 — Weak directional relationship</li>
          <li>0.35 – 0.55 — Moderate structural relationship</li>
          <li>&gt; 0.55 — Strong relationship (rare in macro data)</li>
        </ul>
        <p>Low rho values are common and expected in macro positioning data.</p>
      </div>
      <div className={styles.helpGuideSection}>
        <div className={styles.helpGuideHeading}>Direction of rho (sign matters)</div>
        <div className={styles.helpGuideSubheading}>Positive rho (ρ &gt; 0)</div>
        <p>
          Positioning increases have historically aligned with future price strength → suggests
          trend reinforcement or structural flow alignment.
        </p>
        <div className={styles.helpGuideSubheading}>Negative rho (ρ &lt; 0)</div>
        <p>
          Positioning increases have historically aligned with future price weakness → often
          associated with crowding, late positioning, or fragility.
        </p>
        <p>Negative rho does not imply bearishness — it highlights risk, not direction.</p>
      </div>
      <div className={styles.helpGuideSection}>
        <div className={styles.helpGuideHeading}>Why low rho can still be useful</div>
        <p>Low rho indicates that price has not reliably responded to positioning changes.</p>
        <p>This often means:</p>
        <ul className={styles.helpGuideList}>
          <li>Price is driven by exogenous factors (rates, news, flows)</li>
          <li>Positioning is reactive, not causal</li>
          <li>Macro signals should be down-weighted</li>
        </ul>
        <p>In these conditions, intraday structure and execution signals tend to matter more.</p>
      </div>
      <div className={styles.helpGuideSection}>
        <div className={styles.helpGuideHeading}>Why lead/lag matters</div>
        <p>Positioning updates are slow and discrete, while price is continuous.</p>
        <p>The lead/lag view helps identify whether:</p>
        <ul className={styles.helpGuideList}>
          <li>Positioning tends to lead price</li>
          <li>Positioning tends to lag price</li>
          <li>Or there is no stable relationship</li>
        </ul>
        <p>This exploration improves context — it does not create timing signals.</p>
      </div>
      <div className={styles.helpGuideSection}>
        <div className={`${styles.helpGuideHeading} ${styles.helpGuideHeadingWithIcon}`}>
          <HiOutlineSparkles className={styles.sparklesIcon} />
          How to use rho in this app
        </div>
        <p>Use rho to:</p>
        <ul className={styles.helpGuideList}>
          <li>Assess regime confidence</li>
          <li>Understand structural vs. flow-driven markets</li>
          <li>Decide whether to trust macro context or focus on execution</li>
        </ul>
        <p>Do not use rho to:</p>
        <ul className={styles.helpGuideList}>
          <li>Time entries</li>
          <li>Predict direction</li>
          <li>Override price structure</li>
        </ul>
      </div>
      <div className={styles.helpGuideSection}>
        <div className={styles.helpGuideHeading}>Key takeaway</div>
        <div className={styles.helpGuideQuote}>
          Rho explains whether positioning has mattered historically — not whether it matters right
          now. That distinction is intentional and critical.
        </div>
      </div>
    </>
  );

  const leadLagHelpContent = (
    <>
      <div className={styles.helpGuideTitle}>Help Guide: Using the Lead/Lag Explorer</div>
      <div className={styles.helpGuideSection}>
        <div className={styles.helpGuideHeading}>What is the Lead/Lag Explorer?</div>
        <p>
          The Lead/Lag Explorer helps you understand how changes in CFTC positioning have
          historically related to future price movement over time.
        </p>
        <p>Instead of asking “Does positioning matter?”, it asks:</p>
        <div className={styles.helpGuideQuote}>
          If positioning changes, how long does it typically take before price responds — if at
          all?
        </div>
        <p>This tool is analytical, not predictive.</p>
      </div>
      <div className={styles.helpGuideSection}>
        <div className={styles.helpGuideHeading}>What the Lead/Lag Explorer shows</div>
        <p>The Explorer plots the correlation (ρ) between:</p>
        <ul className={styles.helpGuideList}>
          <li>Positioning changes (from CFTC reports)</li>
          <li>Price returns measured at different time offsets</li>
        </ul>
        <p>Across a range of leads and lags, measured in weeks.</p>
        <div className={styles.helpGuideQuote}>
          If positioning changed at time T, how did price behave N weeks later?
        </div>
      </div>
      <div className={styles.helpGuideSection}>
        <div className={styles.helpGuideHeading}>Understanding the Lead/Lag axis</div>
        <div className={styles.helpGuideSubheading}>Negative values (left side)</div>
        <p>Positioning leads price. Positioning changes occurred before price moved.</p>
        <p>This can suggest:</p>
        <ul className={styles.helpGuideList}>
          <li>Structural positioning influence</li>
          <li>Gradual absorption of flows</li>
          <li>Delayed market response</li>
        </ul>
        <div className={styles.helpGuideSubheading}>Zero (center)</div>
        <p>Simultaneous movement. Positioning and price moved at roughly the same time.</p>
        <p>This often reflects:</p>
        <ul className={styles.helpGuideList}>
          <li>Reactive positioning</li>
          <li>News-driven price action</li>
          <li>Limited causal insight</li>
        </ul>
        <div className={styles.helpGuideSubheading}>Positive values (right side)</div>
        <p>Price leads positioning. Price moved before positioning adjusted.</p>
        <p>This suggests:</p>
        <ul className={styles.helpGuideList}>
          <li>Positioning is reacting to price</li>
          <li>Trend-following behavior</li>
          <li>Reduced informational value of positioning</li>
        </ul>
      </div>
      <div className={styles.helpGuideSection}>
        <div className={styles.helpGuideHeading}>How to configure the Explorer</div>
        <div className={styles.helpGuideSubheading}>1️⃣ Select the horizon</div>
        <p>Choose the maximum lead/lag window (e.g., ±8 weeks).</p>
        <ul className={styles.helpGuideList}>
          <li>Short windows highlight tactical effects</li>
          <li>Longer windows reveal structural tendencies</li>
        </ul>
        <p>There is no “correct” setting — use what matches your holding horizon.</p>
        <div className={styles.helpGuideSubheading}>2️⃣ Choose the return window</div>
        <p>Define how price returns are measured (e.g., 1-week, 2-week, or 4-week returns).</p>
        <ul className={styles.helpGuideList}>
          <li>Short returns → faster reactions</li>
          <li>Longer returns → slower structural moves</li>
        </ul>
        <p>Consistency matters more than optimization.</p>
        <div className={styles.helpGuideSubheading}>3️⃣ Choose the correlation method</div>
        <p>Most users should keep the default:</p>
        <ul className={styles.helpGuideList}>
          <li>Spearman → robust to outliers, monotonic relationships</li>
          <li>Pearson → linear relationships, more sensitive to noise</li>
        </ul>
        <p>Changing methods is for analysis, not edge discovery.</p>
      </div>
      <div className={styles.helpGuideSection}>
        <div className={styles.helpGuideHeading}>How to read the curve</div>
        <div className={styles.helpGuideSubheading}>Look for shape, not peaks</div>
        <p>Do not focus on the highest single point.</p>
        <p>Instead, observe:</p>
        <ul className={styles.helpGuideList}>
          <li>Is the curve flat or structured?</li>
          <li>Does it show persistent sign (positive or negative)?</li>
          <li>Is any relationship stable across adjacent lags?</li>
        </ul>
        <p>Isolated spikes are usually noise.</p>
        <div className={styles.helpGuideSubheading}>What a meaningful pattern looks like</div>
        <p>A useful lead/lag profile typically shows:</p>
        <ul className={styles.helpGuideList}>
          <li>A broad hump, not a sharp spike</li>
          <li>Consistent sign over multiple weeks</li>
          <li>Magnitude above background noise</li>
        </ul>
        <p>Even then, correlations are often modest.</p>
      </div>
      <div className={styles.helpGuideSection}>
        <div className={`${styles.helpGuideHeading} ${styles.helpGuideHeadingWithIcon}`}>
          <HiOutlineSparkles className={styles.sparklesIcon} />
          How to use this in practice
        </div>
        <div className={styles.helpGuideSubheading}>Use it to:</div>
        <ul className={styles.helpGuideList}>
          <li>Assess whether positioning has historically led, lagged, or tracked price</li>
          <li>Decide how much weight to assign to macro context</li>
          <li>Understand whether the market is likely flow-driven or structural</li>
        </ul>
        <div className={styles.helpGuideSubheading}>Do NOT use it to:</div>
        <ul className={styles.helpGuideList}>
          <li>Time entries</li>
          <li>Predict price moves</li>
          <li>Optimize parameters for signals</li>
          <li>Justify trades in isolation</li>
        </ul>
        <p>The Lead/Lag Explorer informs confidence, not timing.</p>
      </div>
      <div className={styles.helpGuideSection}>
        <div className={styles.helpGuideHeading}>How this fits into the platform</div>
        <p>The Lead/Lag Explorer feeds into:</p>
        <ul className={styles.helpGuideList}>
          <li>Trade Readiness</li>
          <li>Regime confidence</li>
          <li>Macro vs. Micro weighting</li>
        </ul>
        <p>
          It helps answer: Should I expect positioning to matter here — or should I rely on price
          structure instead?
        </p>
      </div>
      <div className={styles.helpGuideSection}>
        <div className={styles.helpGuideHeading}>Key takeaway</div>
        <div className={styles.helpGuideQuote}>
          The Lead/Lag Explorer explains when positioning has mattered historically — not whether
          it will matter now. Use it to understand context, not to manufacture certainty.
        </div>
      </div>
    </>
  );

  const rhoHelpModalNode = showRhoHelpModal ? (
    <div className={styles.helpGuideOverlay} role="dialog" aria-modal="true">
      <div className={styles.helpGuideBackdrop} onClick={() => setShowRhoHelpModal(false)} />
      <div className={styles.helpGuideModal}>
        <div className={styles.helpGuideHeader}>
          <div className={styles.helpGuideHeaderTitle}>Positioning vs. Price — ρ Help</div>
          <button
            type="button"
            className={styles.helpGuideClose}
            onClick={() => setShowRhoHelpModal(false)}
          >
            ×
          </button>
        </div>
        <div className={styles.helpGuideTabs}>
          <button
            type="button"
            className={`${styles.helpGuideTab} ${rhoHelpTab === 'rho' ? styles.helpGuideTabActive : ''}`}
            onClick={() => setRhoHelpTab('rho')}
          >
            ρ (Rho)
          </button>
          <button
            type="button"
            className={`${styles.helpGuideTab} ${
              rhoHelpTab === 'leadlag' ? styles.helpGuideTabActive : ''
            }`}
            onClick={() => setRhoHelpTab('leadlag')}
          >
            Lead/Lag
          </button>
        </div>
        <div className={styles.helpGuideBody}>
          <div className={styles.helpGuideCard}>
            {rhoHelpTab === 'rho' ? rhoHelpContent : leadLagHelpContent}
          </div>
        </div>
      </div>
    </div>
  ) : null;

  const correlationChartPanel = (
    <SummaryCard className={styles.summaryCardContent}>
      <div className={styles.rowBetween}>
        <div className={styles.panelTitle}>Lead/Lag Explorer</div>
        <div className={styles.rowWrap} ref={correlationConfidenceRef}>
          <span className={styles.correlationHeaderLabel}>Confidence</span>
          <StatusPill label={correlationConfidence.label} tone="neutral" />
        </div>
      </div>
      <div className={styles.correlationControls}>
        <label className={styles.correlationMenuLabel}>
          Metric
          <select
            ref={correlationMetricRef}
            className={styles.trendSelect}
            value={correlationMetric}
            onChange={(event) => {
              correlationTouchedRef.current = true;
              setCorrelationMetric(event.target.value);
            }}
          >
            {CORRELATION_METRIC_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.correlationMenuLabel}>
          Horizon
          <select
            ref={correlationHorizonRef}
            className={styles.trendSelect}
            value={correlationHorizon}
            onChange={(event) => {
              correlationTouchedRef.current = true;
              setCorrelationHorizon(Number(event.target.value));
            }}
          >
            {CORRELATION_HORIZON_OPTIONS.map((horizon) => (
              <option key={horizon} value={horizon}>
                {horizon}w
              </option>
            ))}
          </select>
        </label>
        <label className={styles.correlationMenuLabel}>
          <span className={styles.correlationLabelWithIcon}>
            Method
            <span className={`${styles.correlationInfoIcon} ${FOCUS_RING}`} tabIndex={0}>
              ?
              <span className={styles.correlationInfoTooltip}>
                <span className={styles.correlationInfoText}>
                  Spearman uses rank correlation. It captures monotonic relationships and is more
                  robust to outliers and regime shifts than Pearson.
                </span>
              </span>
            </span>
          </span>
          <select
            className={styles.trendSelect}
            value={correlationMethod}
            onChange={(event) => {
              correlationTouchedRef.current = true;
              setCorrelationMethod(event.target.value);
            }}
          >
            {CORRELATION_METHOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <div className={styles.correlationSliderGroup}>
          <div className={styles.correlationMenuLabel} ref={correlationLagRef}>
            Lead/Lag
          </div>
          <div className={styles.correlationSliderRow}>
            <input
              type="range"
              min={-8}
              max={8}
              step={1}
              value={correlationLag}
              onChange={(event) => {
                correlationTouchedRef.current = true;
                setCorrelationLag(Number(event.target.value));
              }}
            />
            <span className={styles.correlationLagValue}>
              {correlationLag > 0 ? `+${correlationLag}w` : `${correlationLag}w`}
            </span>
          </div>
          <div className={styles.correlationSliderTicks}>
            <span className={styles.correlationTick}>-8</span>
            <span className={styles.correlationTick}>-4</span>
            <span className={styles.correlationTick}>0</span>
            <span className={styles.correlationTick}>+4</span>
            <span className={styles.correlationTick}>+8</span>
          </div>
          <div className={styles.correlationSliderAnchors}>
            <span>Price leads</span>
            <span>Same week</span>
            <span>Positioning leads</span>
          </div>
        </div>
      </div>
      <div className={styles.correlationSparkline} ref={correlationSparklineRef}>
        <div className={styles.correlationSparklineTitle}>
          Lead/Lag correlation curve
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart
            data={correlationSparklineData}
            margin={{ top: 10, right: 20, bottom: 10, left: 0 }}
            onClick={(event) => {
              const nextLag = event?.activeLabel;
              const parsedLag = Number(nextLag);
              if (Number.isFinite(parsedLag)) {
                correlationTouchedRef.current = true;
                setCorrelationLag(parsedLag);
              }
            }}
          >
            <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" />
            <XAxis dataKey="lag" tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }} />
            <YAxis
              tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
              domain={[-1, 1]}
            />
            <ReferenceLine
              y={0}
              stroke="var(--color-border)"
              strokeDasharray="3 3"
              label={{ value: 'ρ = 0 (no relationship)', position: 'insideTopRight' }}
            />
            {selectedSparklinePoint?.rho != null && (
              <ReferenceDot
                x={selectedSparklinePoint.lag}
                y={selectedSparklinePoint.rho}
                r={4}
                fill="var(--color-accent)"
                stroke="var(--color-accent)"
              />
            )}
            {maxAbsSparklinePoint?.rho != null && (
              <ReferenceDot
                x={maxAbsSparklinePoint.lag}
                y={maxAbsSparklinePoint.rho}
                r={4}
                fill="var(--color-bg-panel)"
                stroke="var(--color-warning)"
              />
            )}
            <Line
              type="monotone"
              dataKey="rho"
              stroke="var(--color-accent)"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
        {maxAbsSparklinePoint?.rho != null && (
          <div className={styles.correlationHint}>
            Max |ρ| across lags: {maxAbsSparklinePoint.rho.toFixed(2)} at{' '}
            {maxAbsSparklinePoint.lag > 0 ? `+${maxAbsSparklinePoint.lag}` : maxAbsSparklinePoint.lag}
            w
          </div>
        )}
      </div>
    </SummaryCard>
  );

  const priceChartNode = (
    <div className={styles.panelCard}>
      <div className={styles.rowBetween}>
        <div className={styles.panelTitle}>Price chart</div>
        <StatusPill
          label={priceReferenceMode === 'live' ? 'Live' : 'Anchored'}
          tone={priceReferenceMode === 'live' ? 'warning' : 'neutral'}
        />
      </div>
      <div className={styles.panelBody}>
        <div className={styles.tradingViewWrap}>
          <div id={tvContainerId.current} className={styles.tradingViewChart} />
        </div>
      </div>
    </div>
  );

  const positioningTourPopover =
    showPositioningTour && positioningTourPosition && activeTourStep ? (
      <div
        className={styles.positioningTourPopover}
        style={{ top: positioningTourPosition.top, left: positioningTourPosition.left }}
      >
        <div className={styles.positioningTourHeading}>How to Read Positioning vs Price</div>
        <div className={styles.positioningTourStep}>
          Step {positioningTourStep + 1} of {positioningTourSteps.length}
        </div>
        <div className={styles.positioningTourTitle}>{activeTourStep.title}</div>
        {activeTourStep.lines?.map((line) => (
          <div key={line} className={styles.positioningTourText}>
            {line}
          </div>
        ))}
        {activeTourStep.example && (
          <div className={styles.positioningTourExample}>{activeTourStep.example}</div>
        )}
        {activeTourStep.boldLine && (
          <div className={styles.positioningTourBold}>{activeTourStep.boldLine}</div>
        )}
        <div className={styles.positioningTourActions}>
          <button type="button" className={styles.positioningTourSkip} onClick={handlePositioningTourSkip}>
            Skip
          </button>
          <div className={styles.positioningTourActionGroup}>
            <button
              type="button"
              className={styles.positioningTourBack}
              onClick={handlePositioningTourBack}
              disabled={positioningTourStep === 0}
            >
              <FaChevronLeft className={styles.tourIcon} />
              Back
            </button>
            <button
              type="button"
              className={styles.positioningTourNext}
              onClick={handlePositioningTourAdvance}
            >
              Next
              <FaChevronRight className={styles.tourIcon} />
            </button>
          </div>
        </div>
      </div>
    ) : null;

  const signalsOnboardingPopover = (
    <SignalsOnboardingTour
      steps={signalsOnboardingSteps}
      stepIndex={signalsOnboarding.step}
      isOpen={signalsOnboarding.isOpen}
      heading="How to use signals"
      onNext={handleSignalsOnboardingNext}
      onBack={handleSignalsOnboardingBack}
      onSkip={handleSignalsOnboardingSkip}
      onDone={handleSignalsOnboardingDone}
      onClose={handleSignalsOnboardingClose}
    />
  );
  const cftcSummaryOnboardingPopover = (
    <SignalsOnboardingTour
      steps={cftcSummaryOnboardingSteps}
      stepIndex={cftcSummaryOnboarding.step}
      isOpen={cftcSummaryOnboarding.isOpen}
      heading="CFTC Summary Guided Walkthrough"
      onNext={handleCftcSummaryOnboardingNext}
      onBack={handleCftcSummaryOnboardingBack}
      onSkip={handleCftcSummaryOnboardingSkip}
      onDone={handleCftcSummaryOnboardingDone}
      onClose={handleCftcSummaryOnboardingClose}
    />
  );
  const positionTrendsOnboardingPopover = (
    <SignalsOnboardingTour
      steps={positionTrendsOnboardingSteps}
      stepIndex={positionTrendsOnboarding.step}
      isOpen={positionTrendsOnboarding.isOpen}
      heading="Position Trend Guided Walkthrough"
      onNext={handlePositionTrendsOnboardingNext}
      onBack={handlePositionTrendsOnboardingBack}
      onSkip={handlePositionTrendsOnboardingSkip}
      onDone={handlePositionTrendsOnboardingDone}
      onClose={handlePositionTrendsOnboardingClose}
    />
  );

  const tickerValue = tvSymbolOverrideByMarket[selectedMarket] ?? tvSymbolDerived ?? '';
  const isTickerEditing = Boolean(tickerEditMap[selectedMarket]);
  const snapshotTickerNode = (
    <div className={styles.snapshotTickerInline}>
      <span className={styles.inlineLabel}>Ticker</span>
      {isTickerEditing ? (
        <input
          className={styles.snapshotTickerInput}
          value={tickerValue}
          onChange={(event) =>
            setTvSymbolOverrideByMarket((prev) => ({
              ...prev,
              [selectedMarket]: event.target.value
            }))
          }
          onBlur={(event) => {
            const trimmed = event.target.value.trim();
            setTvSymbolOverrideByMarket((prev) => ({
              ...prev,
              [selectedMarket]: trimmed
            }));
            setTickerEditMap((prev) => ({ ...prev, [selectedMarket]: false }));
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.currentTarget.blur();
            }
          }}
          placeholder={tvSymbolDerived || 'Enter symbol'}
          aria-label="TradingView ticker override"
        />
      ) : (
        <div className={styles.snapshotTickerDisplay}>
          <span>{tickerValue || '—'}</span>
          <button
            type="button"
            className={styles.snapshotTickerEditButton}
            onClick={() =>
              setTickerEditMap((prev) => ({ ...prev, [selectedMarket]: true }))
            }
            aria-label="Edit ticker"
          >
            <FaPen />
          </button>
        </div>
      )}
    </div>
  );
  const snapshotCftcCode =
    selectedRecord?.CFTC_Contract_Market_Code ||
    selectedRecord?.CFTC_Contract_Market_Code_Quotes ||
    selectedRecord?.CFTC_Market_Code ||
    '';

  const openInterestCardNode = openInterestDistribution ? (
    <div
      className={styles.panelCard}
      data-onboard="open-interest-distribution"
      data-testid="cftc-oi-distribution"
    >
      <div className={styles.panelTitle}>
        Open Interest Distribution
        <span className={`${styles.correlationInfoIcon} ${FOCUS_RING}`} tabIndex={0}>
          ?
          <span className={styles.correlationInfoTooltip}>
            <span className={styles.correlationInfoTitle}>How to read</span>
            <span className={styles.correlationInfoText}>
              Shows each category’s total positions (long + short + spreading) as a share of total
              open interest.
            </span>
          </span>
        </span>
      </div>
      <div className={styles.panelBody}>
        <Plot
          data={[
            {
              type: 'pie',
              values: openInterestDistribution.slices.map((slice) => slice.value),
              labels: openInterestDistribution.slices.map((slice) => slice.label),
              textinfo: 'percent',
              textposition: 'inside',
              hovertext: openInterestDistribution.slices.map(
                (slice) =>
                  `${slice.label}<br>` +
                  `Long: ${formatNumber(slice.long)}<br>` +
                  `Short: ${formatNumber(slice.short)}<br>` +
                  `Spreading: ${formatNumber(slice.spread)}<br>` +
                  `Total: ${formatNumber(slice.value)}`
              ),
              hoverinfo: 'text',
                marker: {
                  colors: openInterestDistribution.slices.map((slice) => {
                    if (slice.label === 'Dealer') return PURCHASER_COLORS.Dealer;
                    if (slice.label === 'Asset Manager') return PURCHASER_COLORS.AssetManager;
                    if (slice.label === 'Leveraged Funds')
                      return PURCHASER_COLORS.LeveragedMoney;
                    if (slice.label === 'Other Reportables') return plotlyWarningColor;
                    if (slice.label === 'Nonreportable') return plotlyAccentColor;
                    return 'var(--color-text-muted)';
                  }),
                },
            },
          ]}
          layout={{
            height: 360,
            margin: { t: 4, r: 4, b: 4, l: 4 },
            paper_bgcolor: plotlyPanelBg,
            plot_bgcolor: plotlyPanelBg,
            font: {
              color: plotlyTextColor,
              size: 16,
              family: 'Inter SemiBold, Inter, system-ui, sans-serif',
            },
            hoverlabel: {
              font: {
                size: 16,
                family: 'Inter SemiBold, Inter, system-ui, sans-serif',
                color: plotlyTextColor,
              },
            },
            legend: {
              orientation: 'h',
              x: 0.5,
              xanchor: 'center',
              y: -0.1,
            },
          }}
          config={{ displayModeBar: false, responsive: true }}
          useResizeHandler
          style={{ width: '100%', height: '100%' }}
        />
      </div>
    </div>
  ) : null;

  const snapshotPanelNode = (
    <div
      className={styles.drawerHeader}
      data-onboard="cftc-snapshot"
      data-testid="cftc-snapshot-header"
    >
      <div>
        <h3>CFTC Snapshot</h3>
        <p>Traders in Financial Futures (Futures + Options Combined)</p>
        {snapshotCftcCode && (
          <div className={styles.snapshotCode}>CFTC Code: {snapshotCftcCode}</div>
        )}
        <div className={styles.snapshotControls} data-testid="cftc-market-selectors">
          <div className={isTrendView ? styles.snapshotColumnStack : styles.snapshotRowTwo}>
            <div className={styles.selectField}>
              <span className={styles.inlineLabel}>Group</span>
              <select value={selectedMarketGroup} onChange={handleMarketGroupChange}>
                {contractGroups.map((group) => (
                  <option key={group.label} value={group.label}>
                    {group.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.selectField}>
              <span className={styles.inlineLabel}>Contract</span>
              <select
                value={selectedMarket}
                onChange={(event) => setSelectedMarket(event.target.value)}
              >
                {selectedGroupOptions.map((market) => (
                  <option key={market} value={market}>
                    {market}
                  </option>
                ))}
              </select>
            </div>
            {isTrendView && snapshotTickerNode}
          </div>
          {!isTrendView && snapshotTickerNode}
          {!isTrendView && (
            <>
              <div className={styles.snapshotRowThree}>
                <div className={styles.selectField}>
                  <span className={styles.inlineLabel}>Year</span>
                  <select
                    value={selectedReportYear}
                    onChange={(event) => setSelectedReportYear(event.target.value)}
                  >
                    {reportYearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.selectField}>
                  <span className={styles.inlineLabel}>Month</span>
                  <select
                    value={selectedReportMonth}
                    onChange={(event) => setSelectedReportMonth(event.target.value)}
                  >
                    {reportMonthOptions.map((month) => (
                      <option key={month} value={month}>
                        {moment(month, 'MM').format('MMM')}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.selectField}>
                  <span className={styles.inlineLabel}>Report Date</span>
                  <select
                    value={selectedReportDate}
                    onChange={(event) => setSelectedReportDate(event.target.value)}
                    data-testid="cftc-report-date"
                  >
                    {reportDateFilteredOptions.map((row) => {
                      const value =
                        row['Report_Date_as_YYYY-MM-DD'] || row.As_of_Date_In_Form_YYMMDD || '';
                      const label = moment(value, ['YYYY-MM-DD', 'YYMMDD'], true).isValid()
                        ? moment(value, ['YYYY-MM-DD', 'YYMMDD'], true).format('MMM D, YYYY')
                        : value;
                      return (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
              <div className={styles.snapshotRow}>
                <details className={styles.advancedDisclosure}>
                  <summary className={styles.advancedSummary}>Advanced</summary>
                  <div className={styles.advancedPanel}>No additional controls.</div>
                </details>
              </div>
            </>
          )}
        </div>
      </div>
      {!isTrendView && selectedDateLabel && (
        <div className={styles.drawerDate}>{selectedDateLabel}</div>
      )}
    </div>
  );

  const impactReportNode = (
    <div className={styles.panelCard} data-testid="cftc-impact-report">
      <div className={styles.rowBetween}>
        <div className={styles.panelTitle}>Positioning Impact Report</div>
        <div className={styles.summaryMuted}>As-of {impactReport.reportDateLabel || '—'}</div>
      </div>
      <div className={styles.panelBody}>
        <div className={styles.impactReportControls}>
          <div className={styles.selectField}>
            <span className={styles.inlineLabel}>Scope</span>
            <select
              value={impactReportScopeDraft}
              onChange={(event) => setImpactReportScopeDraft(event.target.value)}
            >
              {IMPACT_REPORT_SCOPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.selectField}>
            <span className={styles.inlineLabel}>Lookback</span>
            <select
              value={impactReportLookbackDraft}
              onChange={(event) => setImpactReportLookbackDraft(Number(event.target.value))}
            >
              {IMPACT_REPORT_LOOKBACK_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.impactReportGenerateCol}>
            <button
              type="button"
              className={styles.impactReportGenerateButton}
              onClick={handleGenerateImpactReport}
            >
              Generate report
            </button>
            <div className={styles.summaryMuted}>Generated {impactReportGeneratedLabel}</div>
          </div>
        </div>
        <div className={styles.impactReportGroupGrid}>
          {IMPACT_REPORT_PARTICIPANT_GROUPS.map((group) => (
            <label key={group.id} className={styles.impactReportGroupOption}>
              <input
                type="checkbox"
                checked={impactReportGroupIdsDraft.includes(group.id)}
                onChange={() => handleImpactGroupToggle(group.id)}
              />
              <span>{group.label}</span>
            </label>
          ))}
        </div>
        {impactReport.error ? (
          <p className={styles.chartHint}>{impactReport.error}</p>
        ) : (
          <>
            <div className={styles.impactReportSummary}>
              <div className={styles.summaryStrong}>{impactReport.summary}</div>
              <div className={styles.summaryMuted}>
                Groups: {impactReport.groupLabel} • Lookback: {impactReport.lookbackWeeks} week
                {impactReport.lookbackWeeks === 1 ? '' : 's'}
              </div>
            </div>
            {impactReport.shortingMindful.length > 0 && (
              <div className={styles.impactReportSection}>
                <div className={styles.panelTitleSmall}>Mindful With New Shorts</div>
                <ul className={styles.impactReportList}>
                  {impactReport.shortingMindful.map((row) => (
                    <li key={`short-risk-${row.market}-${row.reportDateIso}`}>
                      <strong>{row.market}:</strong> {formatSignedNumber(row.weeklyFlowContracts)} 1W
                      flow ({formatSignedPercent(row.weeklyFlowPctOi)}) with net at{' '}
                      {formatSignedPercent(row.netPctOi)} OI.
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {impactReport.bearishSetups.length > 0 && (
              <div className={styles.impactReportSection}>
                <div className={styles.panelTitleSmall}>Potential Bearish Positioning Support</div>
                <ul className={styles.impactReportList}>
                  {impactReport.bearishSetups.map((row) => (
                    <li key={`bearish-support-${row.market}-${row.reportDateIso}`}>
                      <strong>{row.market}:</strong> flow score {formatSignedPercent(row.flowScorePctOi)} and
                      shorting caution {row.shortingCaution.toLowerCase()}.
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className={styles.tableScroller}>
              <table className={styles.purchaserTable} data-testid="cftc-impact-report-table">
                <thead>
                  <tr>
                    <th>Contract</th>
                    <th>1W ΔNet</th>
                    <th>{impactReport.lookbackWeeks}W ΔNet</th>
                    <th>Net % OI</th>
                    <th>Bias</th>
                    <th>Shorting Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {impactReport.rows.slice(0, 10).map((row) => (
                    <tr key={`impact-row-${row.market}-${row.reportDateIso}`}>
                      <td>{row.market}</td>
                      <td>{formatSignedNumber(row.weeklyFlowContracts)}</td>
                      <td>{formatSignedNumber(row.lookbackFlowContracts)}</td>
                      <td>{formatSignedPercent(row.netPctOi)}</td>
                      <td>{row.bias}</td>
                      <td>
                        <span
                          className={`${styles.impactRiskBadge} ${
                            row.shortingCaution === 'High'
                              ? styles.impactRiskHigh
                              : row.shortingCaution === 'Moderate'
                                ? styles.impactRiskModerate
                                : styles.impactRiskLow
                          }`}
                        >
                          {row.shortingCaution}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.summaryMuted}>
              Educational context only. Validate with price action and predefined risk limits.
            </div>
          </>
        )}
      </div>
    </div>
  );

  const summaryPanelNode = (
    <div className={styles.summaryColumn}>
      <div className={styles.summaryColumnStack}>
        {summaryCardNode}
      </div>
    </div>
  );

  if (isImpactReportView) {
    return (
      <div className={styles.drawer}>
        <div className={styles.snapshotSummaryRowHalf}>
          <div className={styles.snapshotColumn}>{snapshotPanelNode}</div>
        </div>
        {impactReportNode}
      </div>
    );
  }

  if (!isTrendView) {
    return (
      <div className={styles.drawer}>
        <div className={styles.snapshotSummaryControls}>{cftcSummaryGuideLink}</div>
        <div className={styles.snapshotSummaryRowHalf}>
          <div className={styles.snapshotColumn}>{snapshotPanelNode}</div>
          {openInterestCardNode && <div className={styles.snapshotColumn}>{openInterestCardNode}</div>}
        </div>
        {snapshotSummaryNode}
        {positioningTourPopover}
        {signalsOnboardingPopover}
        {cftcSummaryOnboardingPopover}
      </div>
    );
  }

  return (
    <div className={styles.drawer}>
      {headerControlsNode && (
        <div className={styles.snapshotSummaryControls}>{headerControlsNode}</div>
      )}

      <div className={styles.snapshotSummaryGrid}>
        <div className={styles.snapshotColumn}>{snapshotPanelNode}</div>
        {summaryPanelNode}
      </div>

      <CftcDashboardLayout
        summaryPills={null}
        summaryCard={null}
        netPositioningGuide={positionTrendsGuideLink}
        positioningVsPriceGuide={positionVsPriceGuideLink}
        netPositioningHeaderRight={netPositioningHeaderRight}
        netPositioningCardTitle={netPositioningCardTitle}
        netPositioningSectionTitle={null}
        positioningChartSummary={positioningChartSummaryNode}
        deskReadCard={deskReadCardNode}
        positioningChart={positioningChartNode}
        positioningChartBadge={positioningChartBadgeNode}
        correlationSummary={correlationSummaryCard}
        correlationChart={correlationChartPanel}
        priceChart={priceChartNode}
        positioningTitleRef={correlationTitleRef}
      />

      {positioningTourPopover}
      {signalsOnboardingPopover}
      {positionTrendsOnboardingPopover}
      {rhoHelpModalNode}
    </div>
  );
}

export default SideDrawer;
